import {
  lobbyOverlayEl,
  lobbyTitleEl,
  lobbyRoomPillEl,
  lobbyRoomPillLabelEl,
  lobbyIdentityNameEl,
  lobbyIdentityTagEl,
  lobbyIdentityAvatarEl,
  lobbyNavLobbyBtnEl,
  lobbySubtitleEl,
  lobbyCountLabelEl,
  lobbyCountEl,
  lobbyStartRuleLabelEl,
  lobbyStartRuleEl,
  lobbyReadyEl,
  lobbyProgressFillEl,
  lobbyHintEl,
  lobbyCopyBtnEl,
  lobbyReturnGroupEl,
  lobbyRejoinMatchBtnEl,
  lobbyRejoinRoomBtnEl,
  lobbyBackBtnEl,
  lobbyRoomActionsEl,
  lobbyRoomNameInputEl,
  lobbyRoomPasswordInputEl,
  lobbyCreateFormatSelectEl,
  lobbyHostFormatSelectEl,
  lobbyCreateMinSelectEl,
  lobbyCreateMaxSelectEl,
  lobbyCreateModeSelectEl,
  lobbyNewRoomBtnEl,
  lobbyRoomListEl,
  lobbyRoomFiltersEl,
  lobbyRoomSearchInputEl,
  lobbyActionRowEl,
  lobbyHostSettingsGroupEl,
  lobbyHostBotsGroupEl,
  lobbyHostNameInputEl,
  lobbyHostMinSelectEl,
  lobbyHostMaxSelectEl,
  lobbyHostModeToggleEl,
  lobbyHostAddBotBtnEl,
  lobbyHostRemoveBotBtnEl,
  lobbyHostBotCountEl,
  lobbyHostStartBtnEl,
  lobbyHostStartGroupEl,
  lobbyPlayerListEl,
  lobbySpectatorsEl,
  lobbySpectatorCountEl,
  lobbySpectatorPagerEl,
  lobbySpectatorPrevBtnEl,
  lobbySpectatorNextBtnEl,
  lobbySpectatorPageLabelEl,
  lobbySpectateToggleBtnEl,
  lobbyResumeMatchBtnEl,
  lobbySpectatorListEl,
  chatFeedEl,
  chatFormEl,
  sideChatPanelEl,
  lobbyChatEl,
  lobbyChatBodyEl,
  arenaEl,
  authOverlayEl,
  settingsTurnRadiusEl,
  settingsTurnRadiusValueEl
} from '../ui/dom.js';
import {
  sendBye,
  sendColorSelect,
  sendTrailOpacitySelect,
  sendSetTeam,
  sendSpectate,
  sendCancelCountdown
} from '../network-client.js';
import { markAudioInteraction, playCountdownSfx, playSound, stopCountdownSfx } from '../audio/audio.js';
import { SFX } from '../engine/config.js';
import {
  PALETTE_SIZE,
  TRAIL_OPACITY_MIN_PERCENT,
  meshStopsForColorId,
  paletteColorForId,
  paletteNameForId
} from '/shared/game-engine.js';
import {
  normalizePlayerTurnRadiusPercent,
  playerTurnRadiusForPercent
} from '../../../shared/game-balance.js';

// A mesh swatch previews as the same diagonal gradient the arena paints, so
// what you pick in the lobby is what the trail looks like on the field.
function paletteSwatchBackground(colorId) {
  const stops = meshStopsForColorId(colorId);
  if (!stops) return paletteColorForId(colorId);
  return `linear-gradient(135deg, ${stops.join(', ')})`;
}
import { clearSession, readSession } from '../network/session.js';
import { readableTextColor, rgbaFromColor } from '../render/color.js';
import { lobby, lobbyRuntime, net, authState, ui, model, authoritative } from '../engine/state.js';
import { clamp, setTextIfChanged } from '../utils/utils.js';
import { serverNowMs } from '../engine/time.js';
import { roomReadyIsAnnounceable } from '../engine/state-helpers.js';
import { resetChatFeed, applyChatHistory, setRoomPresenceCount } from '../ui/chat.js';
import {
  MIN_TEAM_PLAYERS,
  TEAM_FIRE,
  TEAM_WATER,
  normalizeTeam,
  teamIconClass,
  teamLabel,
  teamShortLabel
} from '/shared/teams.js';
import {
  FULL_PERCENT,
  clampTrailPercent,
  setSlotTrailOpacities
} from '../engine/trail-opacity.js';
import { playerColorById, sameHostName } from '../engine/players.js';
import { setStatus } from '../ui/status.js';
import { syncFocusOverlay } from '../ui/focus-overlay.js';
import { authJson } from '../network/api.js';
import { setLobbyChatDesired, setLobbyRoomsHandler } from '../network/lobby-chat.js';

const lobbyDeps = {
  startNetworking: async () => {},
  disconnect: () => {},
  stopTimers: () => {},
  clearInput: () => {}
};

// The countdown clip's "Go" rings out past the countdown deadline; keep the
// round-start sample suppressed until it has faded.
const COUNTDOWN_GO_TAIL_MS = 1200;
// The waiting room stays up for a beat after the countdown reaches "Go!", so
// the Go reads as the start of the match instead of a frame that flashes past
// on the way to the arena.
//
// Armed from the countdown's own end time while it is still counting, NOT when
// the room_state clearing it arrives: the scene switch is driven by the engine
// status going running, which lands ~90ms after the countdown ends and beats
// that room_state. Arming it late meant the hold was always already expired.
//
// A stopped countdown disarms it (see applyRoomState), so nothing holds when no
// match is coming.
const COUNTDOWN_GO_HOLD_MS = 650;
let countdownEndsAtLocalMs = 0;

function countdownGoHoldRemainingMs() {
  if (countdownEndsAtLocalMs <= 0) return 0;
  return Math.max(0, (countdownEndsAtLocalMs + COUNTDOWN_GO_HOLD_MS) - Date.now());
}
const HOST_SETTINGS_AUTOSAVE_MS = 300;
// Loud enough to carry from a tab the host is not looking at, which is the only
// situation it plays in. ready.mp3 is mastered quietly (peak 0.184), so this
// lands at ~0.17 -- above the in-game one-shots (round-start plays at ~0.09)
// without going anywhere near clipping.
const HOST_READY_SFX_VOLUME = 0.9;

let hostReadyNotified = false;
let hostSettingsAutosaveTimer = 0;
let hostSettingsSaveInFlight = false;
let hostSettingsDraft = null;

function setLobbyDeps(deps = {}) {
  Object.assign(lobbyDeps, deps);
}

function sanitizeRoomId(raw) {
  if (typeof raw !== 'string') return '';
  const normalized = raw.trim().replace(/\s+/g, '-').replace(/[^A-Za-z0-9_-]/g, '');
  return normalized.slice(0, 64);
}

function generateRoomId() {
  const alphabet = 'ABCDEFGHJKLMNPQRSTUVWXYZ23456789';
  let out = '';
  for (let i = 0; i < 6; i++) {
    out += alphabet[Math.floor(Math.random() * alphabet.length)];
  }
  return out;
}

function sanitizeStartMode(rawMode, fallback = 'host') {
  const mode = typeof rawMode === 'string' ? rawMode.toLowerCase() : '';
  if (mode === 'auto' || mode === 'host') return mode;
  return fallback;
}

// Segmented Host Start / Auto Start control. The container's data-mode drives
// the CSS thumb slide while room-setting autosave handles persistence.
function setLobbyStartModeToggle(rawMode) {
  if (!lobbyHostModeToggleEl) return;
  const mode = sanitizeStartMode(rawMode, 'host');
  if (lobbyHostModeToggleEl.dataset.mode === mode) return;
  lobbyHostModeToggleEl.dataset.mode = mode;
  for (const option of lobbyHostModeToggleEl.querySelectorAll('.lobby-startmode-option')) {
    const isActive = option.dataset.mode === mode;
    option.classList.toggle('is-active', isActive);
    option.setAttribute('aria-pressed', isActive ? 'true' : 'false');
  }
}

function sanitizeBotKind(rawKind) {
  const kind = typeof rawKind === 'string' ? rawKind.toLowerCase() : '';
  if (kind === 'manual' || kind === 'player') return kind;
  return 'none';
}

function clampRoomPlayers(value, fallback = 2, min = 1, max = 8) {
  const parsed = Number.parseInt(value, 10);
  if (!Number.isFinite(parsed)) return fallback;
  return clamp(parsed, min, max);
}

function normalizeRoomName(rawName, fallback = '') {
  const normalized = typeof rawName === 'string' ? rawName.trim().slice(0, 48) : '';
  if (normalized) return normalized;
  return typeof fallback === 'string' ? fallback.trim().slice(0, 48) : '';
}

function normalizeHostRoomSettings(settings = {}) {
  const nextMax = clampRoomPlayers(settings.maxPlayers, lobby.maxPlayers);
  const nextMin = Math.min(
    nextMax,
    clampRoomPlayers(settings.minPlayersToStart, lobby.minPlayersToStart)
  );
  return {
    name: normalizeRoomName(settings.name, lobby.roomName || lobby.roomId),
    minPlayersToStart: nextMin,
    maxPlayers: nextMax,
    startMode: sanitizeStartMode(settings.startMode, lobby.startMode || 'host'),
    teamMode: Boolean(settings.teamMode)
  };
}

function hostRoomSettingsKey(settings) {
  if (!settings) return '';
  return [
    settings.name,
    settings.minPlayersToStart,
    settings.maxPlayers,
    settings.startMode,
    settings.teamMode ? 't' : 'f'
  ].join('|');
}

function currentHostRoomSettings() {
  return normalizeHostRoomSettings({
    name: lobby.roomName,
    minPlayersToStart: lobby.minPlayersToStart,
    maxPlayers: lobby.maxPlayers,
    startMode: lobby.startMode,
    teamMode: lobby.teamMode
  });
}

// Match format is a preset over the two player-count knobs rather than a
// setting of its own: nothing new crosses the wire, and min/max stay the
// source of truth (and the escape hatch).
//
// Counts that match no preset read as FFA. There used to be a hidden "Custom"
// option for them, and it made the min/max controls look broken: nudging an
// FFA room's minimum from 2 to 1 is still a free-for-all, but the format
// silently flipped to Custom, so a label meant to describe the room instead
// announced that you had left the format behind. FFA is the general case here
// -- solo and 1v1 are the two exact shapes worth naming, and everything else
// that is not a team game is a free-for-all -- so the fallback names it.
// Teams is the one format that is NOT just a count preset: the sides are
// assigned server-side and the room has to refuse to start on an odd roster,
// neither of which can be derived from min/max. So it carries a flag as well,
// and the flag is what distinguishes it from an FFA of the same size.
const MATCH_FORMATS = {
  solo: { minPlayersToStart: 1, maxPlayers: 1, teamMode: false },
  duel: { minPlayersToStart: 2, maxPlayers: 2, teamMode: false },
  // Opens at 1, not 2: an FFA room should start when its host is ready rather
  // than stall waiting for a second person who may never arrive. What the match
  // actually COUNTS as is decided at the end from who really played -- one
  // player scores on the Solo board, two on 1v1 -- so starting alone costs
  // nothing and does not mint an FFA record out of a solo run.
  ffa: { minPlayersToStart: 1, maxPlayers: 8, teamMode: false },
  // Opens at 1 for the same reason FFA does, and it matters more here: gated at
  // 4, a team room waits for a full 2v2 before anything can happen, so the
  // short-handed fallback (1 plays solo, 2 plays a 1v1) could never be reached.
  // Capacity still has to be even -- that is the part sides actually require.
  teams: { minPlayersToStart: 1, maxPlayers: 8, teamMode: true }
};

function matchFormatForCounts(minPlayersToStart, maxPlayers, teamMode = false) {
  const min = Number(minPlayersToStart);
  const max = Number(maxPlayers);
  for (const [key, preset] of Object.entries(MATCH_FORMATS)) {
    if (Boolean(preset.teamMode) !== Boolean(teamMode)) continue;
    if (preset.minPlayersToStart === min && preset.maxPlayers === max) return key;
  }
  // A team room with hand-picked counts is still a team room: teamMode is the
  // one thing the dropdown cannot re-derive from the counts, so it decides.
  return teamMode ? 'teams' : 'ffa';
}

function syncMatchFormatSelect(formatEl, minPlayersToStart, maxPlayers, teamMode = false) {
  if (!formatEl) return;
  formatEl.value = matchFormatForCounts(minPlayersToStart, maxPlayers, teamMode);
}

// Applies a preset to a min/max select pair. Returns false for a value that is
// not a preset at all, so callers can ignore a change that carries no counts.
// Every option is a real preset now that Custom is gone, so this is a guard
// rather than a path anything takes.
function applyMatchFormatToSelects(formatEl, minEl, maxEl) {
  const preset = MATCH_FORMATS[formatEl?.value];
  if (!preset) return false;
  if (minEl) minEl.value = String(preset.minPlayersToStart);
  if (maxEl) maxEl.value = String(preset.maxPlayers);
  return true;
}

function readHostRoomSettingsControls() {
  return normalizeHostRoomSettings({
    name: lobbyHostNameInputEl?.value,
    minPlayersToStart: lobbyHostMinSelectEl?.value,
    maxPlayers: lobbyHostMaxSelectEl?.value,
    startMode: lobbyHostModeToggleEl?.dataset.mode,
    teamMode: lobbyHostFormatSelectEl?.value === 'teams'
  });
}

// A team room's counts are even by construction -- seats alternate, so an odd
// capacity leaves one side permanently short and 3v2 is not a shape a team
// match has. The server rounds them down anyway; hiding the odd options is what
// stops the dropdown from offering a choice that would silently become another
// one, which reads as the control being broken.
// CAPACITY is what has to be even and at least 2v2: seats alternate between the
// sides, so an odd or smaller ceiling leaves one side permanently short and the
// room could never fill into a fair game.
//
// The MINIMUM is not gated, because it is not a statement about sides -- it is
// how many people have to show up before play begins. A team room set to start
// at 1 opens as a solo run and becomes a real 2v2 once four are in; gating it
// to 4 is what used to leave a two-person team room sitting there unable to
// start anything. Mirrors enforceEvenTeamBounds on the server.
function applyTeamCountOptionGate(minSelectEl, maxSelectEl, teamMode) {
  for (const option of minSelectEl?.options || []) {
    option.hidden = false;
    option.disabled = false;
  }
  for (const option of maxSelectEl?.options || []) {
    const value = Number(option.value);
    const allowed = !teamMode || (value % 2 === 0 && value >= MIN_TEAM_PLAYERS);
    option.hidden = !allowed;
    option.disabled = !allowed;
  }
}

function syncCreateTeamCountOptions() {
  const teamMode = lobbyCreateFormatSelectEl?.value === 'teams';
  applyTeamCountOptionGate(lobbyCreateMinSelectEl, lobbyCreateMaxSelectEl, teamMode);
}

function syncTeamPlayerCountOptions(teamMode) {
  applyTeamCountOptionGate(lobbyHostMinSelectEl, lobbyHostMaxSelectEl, teamMode);
}

function applyHostRoomSettingsControls(settings = null) {
  const nextSettings = settings || hostSettingsDraft || currentHostRoomSettings();
  if (
    lobbyHostNameInputEl
    && document.activeElement !== lobbyHostNameInputEl
    && lobbyHostNameInputEl.value !== nextSettings.name
  ) {
    lobbyHostNameInputEl.value = nextSettings.name;
  }
  if (lobbyHostMinSelectEl) {
    lobbyHostMinSelectEl.value = String(nextSettings.minPlayersToStart);
  }
  if (lobbyHostMaxSelectEl) {
    lobbyHostMaxSelectEl.value = String(nextSettings.maxPlayers);
  }
  syncTeamPlayerCountOptions(nextSettings.teamMode);
  syncMatchFormatSelect(
    lobbyHostFormatSelectEl,
    nextSettings.minPlayersToStart,
    nextSettings.maxPlayers,
    nextSettings.teamMode
  );
  setLobbyStartModeToggle(nextSettings.startMode);
}

function setHostRoomSettingsControlsDisabled(disabled) {
  const shouldDisable = Boolean(disabled);
  if (lobbyHostNameInputEl) {
    lobbyHostNameInputEl.disabled = shouldDisable || authState.guest;
    lobbyHostNameInputEl.title = authState.guest
      ? 'Sign in with a passkey to rename rooms'
      : 'Room name (autosaves)';
  }
  if (lobbyHostFormatSelectEl) lobbyHostFormatSelectEl.disabled = shouldDisable;
  if (lobbyHostMinSelectEl) lobbyHostMinSelectEl.disabled = shouldDisable;
  if (lobbyHostMaxSelectEl) lobbyHostMaxSelectEl.disabled = shouldDisable;
  if (lobbyHostModeToggleEl) {
    for (const option of lobbyHostModeToggleEl.querySelectorAll('.lobby-startmode-option')) {
      option.disabled = shouldDisable;
    }
  }
}

function clearHostRoomSettingsAutosave() {
  if (hostSettingsAutosaveTimer) {
    clearTimeout(hostSettingsAutosaveTimer);
    hostSettingsAutosaveTimer = 0;
  }
  hostSettingsDraft = null;
  hostSettingsSaveInFlight = false;
}

function scheduleHostRoomSettingsAutosave() {
  if (!lobby.isHost || lobby.phase === 'running') return;
  if (hostSettingsAutosaveTimer) {
    clearTimeout(hostSettingsAutosaveTimer);
  }
  hostSettingsAutosaveTimer = window.setTimeout(() => {
    hostSettingsAutosaveTimer = 0;
    void flushHostRoomSettingsAutosave();
  }, HOST_SETTINGS_AUTOSAVE_MS);
}

function queueHostRoomSettingsAutosave() {
  if (!lobby.isHost || lobby.phase === 'running') return;
  hostSettingsDraft = readHostRoomSettingsControls();
  applyHostRoomSettingsControls(hostSettingsDraft);
  if (hostRoomSettingsKey(hostSettingsDraft) === hostRoomSettingsKey(currentHostRoomSettings())) {
    hostSettingsDraft = null;
    return;
  }
  scheduleHostRoomSettingsAutosave();
}

// Typing a multi-word name necessarily creates a trailing space for a moment.
// Do not run the ordinary control sync on every keystroke: normalization trims
// that temporary space and would make it impossible to type the next word.
function queueHostRoomNameAutosave() {
  if (!lobby.isHost || lobby.phase === 'running' || authState.guest) return;
  hostSettingsDraft = readHostRoomSettingsControls();
  scheduleHostRoomSettingsAutosave();
}

async function flushHostRoomSettingsAutosave() {
  if (!lobby.isHost || lobby.phase === 'running' || !hostSettingsDraft) return;
  if (hostSettingsSaveInFlight) {
    scheduleHostRoomSettingsAutosave();
    return;
  }
  const settings = hostSettingsDraft;
  const settingsKey = hostRoomSettingsKey(settings);
  if (settingsKey === hostRoomSettingsKey(currentHostRoomSettings())) {
    hostSettingsDraft = null;
    renderLobby();
    return;
  }

  hostSettingsSaveInFlight = true;
  try {
    await updateRoomSettingsViaApi(lobby.roomId, settings);
    if (hostSettingsDraft && hostRoomSettingsKey(hostSettingsDraft) === settingsKey) {
      lobby.roomName = settings.name;
      lobby.maxPlayers = settings.maxPlayers;
      lobby.minPlayersToStart = settings.minPlayersToStart;
      lobby.startMode = settings.startMode;
      lobby.teamMode = settings.teamMode;
      lobby.canStart = lobby.connectedPlayers >= lobby.minPlayersToStart;
      hostSettingsDraft = null;
    }
  } catch (error) {
    if (hostSettingsDraft && hostRoomSettingsKey(hostSettingsDraft) === settingsKey) {
      lobby.message = `Autosave failed: ${error?.message || 'unknown error'}`;
    }
  } finally {
    hostSettingsSaveInFlight = false;
    if (hostSettingsDraft && hostRoomSettingsKey(hostSettingsDraft) !== settingsKey) {
      scheduleHostRoomSettingsAutosave();
    }
    renderLobby();
  }
}

function buildLobbyColorIds(slots) {
  const humanCount = slots.filter((player) => player && !Boolean(player.isBot)).length;
  let nextBotId = humanCount;
  return slots.map((player) => {
    if (!player) return null;
    const playerId = Number(player.playerId);
    if (!Boolean(player.isBot) && Number.isFinite(playerId) && playerId >= 0) {
      return Math.floor(playerId);
    }
    const botId = nextBotId;
    nextBotId += 1;
    return botId;
  });
}

function slotTrailOpacityKey(player) {
  if (!player || sanitizeBotKind(player.botKind) !== 'manual') return null;
  const botIndex = Number(player.botIndex);
  return Number.isInteger(botIndex) && botIndex >= 0 ? botIndex : null;
}

// Room state is the authority for every slot, including this browser's player.
function lobbySlotTrailPercent(player) {
  return clampTrailPercent(player?.trailOpacityPercent ?? FULL_PERCENT);
}

function setLobbySlotTrailPercent(player, percent) {
  const value = clampTrailPercent(percent);
  const botIndex = slotTrailOpacityKey(player);
  if (!isOwnLobbySlot(player) && botIndex === null) return false;
  if (!sendTrailOpacitySelect(value, botIndex)) {
    lobby.message = 'Could not change trail opacity while disconnected.';
    return false;
  }
  // Optimistic only: the server echoes the accepted value to every client and
  // future room states remain authoritative.
  player.trailOpacityPercent = value;
  publishSlotTrailOpacities();
  return true;
}

// Translate the server roster's stable slot style to engine player ids, the ids
// carried by trail operations. Re-derived whenever roster/style state changes.
function publishSlotTrailOpacities() {
  const slots = Array.isArray(lobby.players) ? lobby.players.filter(Boolean) : [];
  const colorIds = buildLobbyColorIds(slots);
  const entries = [];
  slots.forEach((player, index) => {
    const percent = lobbySlotTrailPercent(player);
    if (percent >= FULL_PERCENT) return;
    const engineId = Number(colorIds[index]);
    if (!Number.isFinite(engineId) || engineId < 0) return;
    entries.push([engineId, percent]);
  });
  setSlotTrailOpacities(entries);
}

// The title over a side's column. It is also how you change sides: clicking it
// asks for a seat on that team, which the server grants only if one is free --
// the seats ARE the sides, so a full team simply has nowhere to put you.
function buildLobbyTeamHead(team, slots) {
  const head = document.createElement('li');
  head.className = 'lobby-team-head';
  head.dataset.team = team;

  const icon = document.createElement('i');
  icon.className = `bi ${teamIconClass(team)}`;
  icon.setAttribute('aria-hidden', 'true');

  const label = document.createElement('span');
  label.className = 'lobby-team-head-name';
  label.textContent = teamLabel(team);

  head.append(icon, label);

  const mine = (Array.isArray(slots) ? slots : []).find((slot) => slot && isOwnLobbySlot(slot));
  const onThisTeam = normalizeTeam(mine?.team) === team;
  const seats = (Array.isArray(slots) ? slots : [])
    .map((slot, index) => ({ slot, index }))
    .filter(({ index }) => (index % 2 === 0 ? TEAM_FIRE : TEAM_WATER) === team);
  // A seat is available if it is empty OR held by one of the host's bots: the
  // server re-seats bots around a player's pick, so offering only empty seats
  // would refuse a switch it would actually grant. A side full of PEOPLE is the
  // only side that is really full.
  const hasRoom = seats.some(({ slot }) => (
    !slot || (slot.isBot && sanitizeBotKind(slot.botKind) === 'manual')
  ));
  const canSwitch = Boolean(mine)
    && !onThisTeam
    && hasRoom
    && lobby.phase !== 'running'
    && lobby.phase !== 'starting';

  head.classList.toggle('is-mine', onThisTeam);
  if (canSwitch) {
    head.classList.add('is-switchable');
    head.title = `Switch to ${teamLabel(team)}`;
    head.setAttribute('role', 'button');
    head.setAttribute('tabindex', '0');
    const join = () => {
      if (!net.connected) return;
      if (!sendSetTeam(team)) {
        lobby.message = 'Could not switch teams while disconnected.';
        renderLobby();
      }
    };
    head.addEventListener('click', join);
    head.addEventListener('keydown', (event) => {
      if (event.key !== 'Enter' && event.key !== ' ') return;
      event.preventDefault();
      join();
    });
  } else if (mine && !onThisTeam && !hasRoom) {
    head.title = `${teamLabel(team)} is full`;
  }
  return head;
}

function applyLobbyPlayerColor(row, color, player = null) {
  row.style.setProperty('--lobby-player-color', color);
  row.style.setProperty('--lobby-player-color-soft', rgbaFromColor(color, 0.14));
  row.style.setProperty('--lobby-player-color-border', rgbaFromColor(color, 0.58));
  row.style.setProperty('--lobby-player-ink', readableTextColor(color));
  // The chip is the slot's stand-in for the curve, so it carries the alpha the
  // room server publishes for that slot.
  // Its own variable: --lobby-player-color also drives the row's edge stripe
  // and the bot icon, which are chrome rather than a preview of the trail.
  const percent = player ? lobbySlotTrailPercent(player) : FULL_PERCENT;
  row.style.setProperty('--lobby-player-chip-fill', rgbaFromColor(color, percent / 100));
  const key = player ? lobbyColorPickerSlotKey(player) : '';
  if (key) row.dataset.trailSlot = key;
  else delete row.dataset.trailSlot;
}

// Is this roster slot the one whose trail you are about to draw? A self-bot
// slot still is: the bot steers, but the trail is still yours to look at.
//
// The engine id decides it whenever there is one, and the display name is only
// the fallback for before a match has assigned you one. Names are not a stable
// identity here -- the server renames a joiner whose name is already taken, and
// this asked two different questions of the same slot when it did: the roster
// row said "mine" and the picker opened on it said "not mine", so a drag on
// your own bar silently did nothing.
function isOwnLobbySlot(player) {
  if (!player?.isClientSlot) return false;
  const playerId = Number(player.playerId);
  const localId = Number(net.localPlayerId);
  if (Number.isFinite(playerId) && playerId >= 0 && Number.isFinite(localId) && localId >= 0) {
    return playerId === localId;
  }
  const ownerName = typeof player.ownerName === 'string' && player.ownerName.trim()
    ? player.ownerName.trim()
    : (typeof player.name === 'string' ? player.name.trim() : '');
  return sameHostName(ownerName, net.playerName);
}

// Repaints one slot's chip at its new alpha without going through renderLobby:
// that would rebuild the open colour picker, and the picker is where the alpha
// is being dragged from -- replacing the range input mid-drag would drop the
// pointer capture and strand the gesture.
function refreshLobbyPlayerChipAlpha(player) {
  if (!lobbyPlayerListEl) return;
  const wanted = lobbyColorPickerSlotKey(player);
  if (!wanted) return;
  const alpha = lobbySlotTrailPercent(player) / 100;
  for (const row of lobbyPlayerListEl.children) {
    if (row.dataset?.trailSlot !== wanted) continue;
    const color = row.style?.getPropertyValue('--lobby-player-color')?.trim();
    if (!color) continue;
    row.style.setProperty('--lobby-player-chip-fill', rgbaFromColor(color, alpha));
  }
}

// --- Color picker: a radial swatch burst anchored to your slot chip. ---

let lobbyColorPopEl = null;
let lobbyColorPickSentAt = 0;
let lobbyPendingColorPick = null;

function closeLobbyColorPicker() {
  if (!lobbyColorPopEl) return;
  const pop = lobbyColorPopEl;
  lobbyColorPopEl = null;
  if (pop._onDismiss) {
    document.removeEventListener('pointerdown', pop._onDismiss, true);
    document.removeEventListener('keydown', pop._onEscape, true);
  }
  pop.remove();
}

function toggleLobbyColorPicker(chip, player) {
  if (lobbyColorPopEl) {
    closeLobbyColorPicker();
    return;
  }
  openLobbyColorPicker(chip, player);
}

function selectLobbyPlayerColor(player, colorId) {
  if (!player || !Number.isInteger(colorId) || colorId < 0 || colorId >= PALETTE_SIZE) {
    return false;
  }
  // Manual bots carry their bot index; the host recolors them through the
  // same server-authoritative message with botIndex attached.
  const isManualBot = sanitizeBotKind(player.botKind) === 'manual';
  const botIndex = isManualBot && Number.isInteger(player.botIndex) ? player.botIndex : null;
  if (isManualBot && botIndex === null) return false;
  if (!sendColorSelect(colorId, botIndex)) {
    closeLobbyColorPicker();
    lobby.message = 'Could not change color while disconnected.';
    renderLobby();
    return false;
  }

  lobbyColorPickSentAt = Date.now();
  lobbyPendingColorPick = {
    player,
    colorId,
    previousColorId: player.colorId,
    previousColor: player.color,
    previousColorChosen: player.colorChosen
  };

  // Paint the exact roster slot that opened the picker immediately. The next
  // room state remains authoritative and will confirm (or replace) this view.
  player.colorId = colorId;
  player.color = paletteColorForId(colorId);
  player.mesh = meshStopsForColorId(colorId);
  player.colorChosen = true;
  closeLobbyColorPicker();
  renderLobby();
  return true;
}

// Identifies a roster slot across room_state rebuilds. `lobby.players` is
// replaced wholesale on every update, so object identity cannot be used to
// find "the same player" a moment later.
function lobbyColorPickerSlotKey(player) {
  if (!player) return '';
  const botIndex = Number.isInteger(player.botIndex) ? player.botIndex : -1;
  if (sanitizeBotKind(player.botKind) === 'manual' && botIndex >= 0) return `bot:${botIndex}`;
  if (player.isClientSlot) {
    const ownerName = String(player.ownerName || player.name || '').trim().toLowerCase();
    if (ownerName) return `client:${ownerName}`;
  }
  if (Number.isInteger(player.playerId) && player.playerId >= 0) return `pid:${player.playerId}`;
  return `name:${String(player.name || '').trim().toLowerCase()}`;
}

// Builds the picker's contents. Split out from opening it so the open picker
// can be rebuilt in place on every roster change: the pop lives on
// document.body, so re-rendering the lobby never touched it, and a color
// somebody else took stayed clickable until you closed and reopened.
function fillLobbyColorPicker(pop, player) {
  pop.replaceChildren();

  const others = Array.isArray(lobby.players)
    ? lobby.players.filter((slot) => slot && lobbyColorPickerSlotKey(slot) !== lobbyColorPickerSlotKey(player))
    : [];
  // Colors currently on other players' rows, chosen or auto-assigned: the
  // dice never lands on one of those, nor on your own current color.
  const occupiedIds = new Set(
    others.map((slot) => slot.colorId).filter((id) => Number.isInteger(id))
  );

  // Preview: a bar in the highlighted color with its name underneath,
  // falling back to the player's current color when nothing is hovered.
  //
  // The bar doubles as the trail-opacity control -- drag it left and right.
  // It belongs here rather than in the settings modal because the thing it
  // adjusts is exactly what the bar already shows: the fill is painted at the
  // chosen alpha over an arena-dark backing, so the bar IS the preview of the
  // trail you are about to draw. A real range input does the interaction so
  // dragging, click-to-jump and arrow keys all come for free; it is
  // transparent and stretched over the bar, with the thumb drawn separately.
  //
  // It edits the exact server-owned slot this picker was opened on. Human
  // players control their own slot; hosts can also control their manual bots.
  const previewBar = document.createElement('div');
  previewBar.className = 'lobby-color-preview-bar';
  const previewFill = document.createElement('div');
  previewFill.className = 'lobby-color-preview-fill';
  const previewThumb = document.createElement('div');
  previewThumb.className = 'lobby-color-preview-thumb';
  const previewReadout = document.createElement('div');
  previewReadout.className = 'lobby-color-preview-readout';
  const opacityRange = document.createElement('input');
  opacityRange.type = 'range';
  opacityRange.className = 'lobby-color-preview-range';
  opacityRange.min = String(TRAIL_OPACITY_MIN_PERCENT);
  opacityRange.max = '100';
  opacityRange.step = '1';
  const ownSlot = isOwnLobbySlot(player);
  opacityRange.title = ownSlot
    ? 'Drag to set your trail opacity (visible to everyone)'
    : `Drag to set ${player?.name || 'this'} trail opacity (visible to everyone)`;
  opacityRange.setAttribute('aria-label', ownSlot ? 'Your trail opacity' : 'Bot trail opacity');
  previewBar.append(previewFill, previewThumb, previewReadout, opacityRange);

  const renderTrailOpacity = () => {
    const pct = lobbySlotTrailPercent(player);
    opacityRange.value = String(pct);
    previewFill.style.opacity = String(pct / 100);
    previewReadout.textContent = `${Math.round(pct)}%`;
    // Position the thumb across the usable span, not 0..100.
    const span = 100 - TRAIL_OPACITY_MIN_PERCENT;
    previewThumb.style.left = span > 0
      ? `${((pct - TRAIL_OPACITY_MIN_PERCENT) / span) * 100}%`
      : '100%';
  };
  const onOpacityInput = () => {
    setLobbySlotTrailPercent(player, Number(opacityRange.value));
    renderTrailOpacity();
    refreshLobbyPlayerChipAlpha(player);
  };
  opacityRange.addEventListener('input', onOpacityInput);
  opacityRange.addEventListener('change', onOpacityInput);
  // The picker dismisses on pointerdown outside it; the range must not let that
  // reach the document while you are dragging along the bar.
  opacityRange.addEventListener('pointerdown', (event) => event.stopPropagation());
  renderTrailOpacity();

  const previewName = document.createElement('div');
  previewName.className = 'lobby-color-preview-name';
  pop.appendChild(previewBar);
  pop.appendChild(previewName);

  // The bar takes the gradient, the label takes the flat base: `color:` has no
  // valid gradient form, so passing one would silently drop the label's color.
  const setPreview = (background, textColor, label, taken) => {
    previewFill.style.background = background;
    previewName.style.color = textColor;
    previewName.textContent = label;
    pop.classList.toggle('preview-taken', Boolean(taken));
  };
  const resetPreview = () => {
    const id = player.colorId;
    const known = Number.isInteger(id) && id >= 0 && id < PALETTE_SIZE;
    const background = known ? paletteSwatchBackground(id) : (player.color || '#aebedb');
    const textColor = known ? paletteColorForId(id) : (player.color || '#aebedb');
    setPreview(background, textColor, known ? paletteNameForId(id) : textColor, false);
  };
  resetPreview();

  const grid = document.createElement('div');
  grid.className = 'lobby-color-grid';
  pop.appendChild(grid);

  for (let id = 0; id < PALETTE_SIZE; id++) {
    const color = paletteSwatchBackground(id);
    const name = paletteNameForId(id);
    const takenBy = others.find((slot) => slot.colorChosen && slot.colorId === id);
    const isCurrent = player.colorId === id;

    const swatch = document.createElement('button');
    swatch.type = 'button';
    swatch.className = 'lobby-color-swatch';
    swatch.style.background = color;
    if (meshStopsForColorId(id)) swatch.classList.add('is-mesh');
    swatch.style.setProperty('--swatch-delay', `${id * 18}ms`);
    swatch.title = takenBy ? `${name} — taken by ${takenBy.name}` : name;

    if (isCurrent) swatch.classList.add('current');
    if (takenBy) {
      swatch.classList.add('taken');
      swatch.disabled = true;
    } else {
      swatch.addEventListener('click', (event) => {
        event.preventDefault();
        event.stopPropagation();
        selectLobbyPlayerColor(player, id);
      });
    }
    swatch.addEventListener('mouseenter', () => {
      setPreview(color, paletteColorForId(id), takenBy ? `${name} — taken` : name, Boolean(takenBy));
    });
    swatch.addEventListener('mouseleave', resetPreview);
    grid.appendChild(swatch);
  }

  const dice = document.createElement('button');
  dice.type = 'button';
  dice.className = 'lobby-color-dice';
  dice.textContent = '🎲';
  dice.title = 'Roll a random free color';
  dice.style.setProperty('--swatch-delay', `${PALETTE_SIZE * 18}ms`);
  dice.addEventListener('click', (event) => {
    event.preventDefault();
    event.stopPropagation();
    const pool = [];
    for (let id = 0; id < PALETTE_SIZE; id++) {
      if (!occupiedIds.has(id) && id !== player.colorId) pool.push(id);
    }
    if (pool.length === 0) return;
    const id = pool[Math.floor(Math.random() * pool.length)];
    selectLobbyPlayerColor(player, id);
  });
  grid.appendChild(dice);
}

// Called from renderLobby, i.e. on every room_state.
function refreshLobbyColorPicker() {
  const pop = lobbyColorPopEl;
  if (!pop) return;
  const player = (Array.isArray(lobby.players) ? lobby.players : [])
    .find((slot) => lobbyColorPickerSlotKey(slot) === pop._slotKey);
  // The slot we were recoloring is gone (kicked, left, bot removed).
  if (!player) {
    closeLobbyColorPicker();
    return;
  }
  fillLobbyColorPicker(pop, player);
}

function openLobbyColorPicker(chip, player) {
  closeLobbyColorPicker();

  const pop = document.createElement('div');
  pop.className = 'lobby-color-pop';
  pop._slotKey = lobbyColorPickerSlotKey(player);
  fillLobbyColorPicker(pop, player);

  document.body.appendChild(pop);
  const rect = chip.getBoundingClientRect();
  const popW = pop.offsetWidth || 200;
  const popH = pop.offsetHeight || 140;
  const left = clamp(rect.left + rect.width / 2 - popW / 2, 8, window.innerWidth - popW - 8);
  let top = rect.bottom + 8;
  if (top + popH > window.innerHeight - 8) top = rect.top - popH - 8;
  top = clamp(top, 8, window.innerHeight - popH - 8);
  pop.style.left = `${left}px`;
  pop.style.top = `${top}px`;

  pop._onDismiss = (event) => {
    // Let the anchor chip's own click handler run the toggle, otherwise
    // dismissing here would make that click immediately reopen the picker.
    if (pop.contains(event.target) || chip.contains(event.target)) return;
    closeLobbyColorPicker();
  };
  pop._onEscape = (event) => {
    if (event.key === 'Escape') closeLobbyColorPicker();
  };
  document.addEventListener('pointerdown', pop._onDismiss, true);
  document.addEventListener('keydown', pop._onEscape, true);

  lobbyColorPopEl = pop;
}

// --- Host moderation menu: kick / mute / ban / move to spectators. ---

let lobbyPlayerMenuEl = null;

function closeLobbyPlayerMenu() {
  if (!lobbyPlayerMenuEl) return;
  const pop = lobbyPlayerMenuEl;
  lobbyPlayerMenuEl = null;
  if (pop._onDismiss) {
    document.removeEventListener('pointerdown', pop._onDismiss, true);
    document.removeEventListener('keydown', pop._onEscape, true);
  }
  pop.remove();
}

function toggleLobbyPlayerMenu(chip, player) {
  if (lobbyPlayerMenuEl) {
    closeLobbyPlayerMenu();
    return;
  }
  openLobbyPlayerMenu(chip, player);
}

async function moderatePlayerViaApi(roomId, action, targetName) {
  const sanitizedRoomId = sanitizeRoomId(roomId);
  if (!sanitizedRoomId) throw new Error('Invalid room ID');
  const encodedRoomId = encodeURIComponent(sanitizedRoomId);
  const data = await authJson(`/api/rooms/${encodedRoomId}/moderate`, 'POST', {
    actorName: net.playerName,
    action,
    targetName
  }, hostAuth(sanitizedRoomId));
  return data?.room || null;
}

function runModerationAction(action, targetName, pendingMessage) {
  closeLobbyPlayerMenu();
  if (lobbyRuntime.botActionInFlight) return;
  lobbyRuntime.botActionInFlight = true;
  lobby.message = pendingMessage;
  renderLobby();
  moderatePlayerViaApi(lobby.roomId, action, targetName)
    .catch((error) => {
      lobby.message = `Could not ${action} ${targetName}: ${error?.message || 'unknown error'}`;
    })
    .finally(() => {
      lobbyRuntime.botActionInFlight = false;
      renderLobby();
    });
}

function openLobbyPlayerMenu(chip, player) {
  closeLobbyPlayerMenu();
  closeLobbyColorPicker();

  const targetName = typeof player.ownerName === 'string' && player.ownerName.trim()
    ? player.ownerName.trim()
    : (typeof player.name === 'string' ? player.name.trim() : '');
  if (!targetName) return;

  const pop = document.createElement('div');
  pop.className = 'lobby-player-menu';

  const head = document.createElement('div');
  head.className = 'lobby-player-menu-head';
  head.textContent = player.name || targetName;
  pop.appendChild(head);

  const muted = Boolean(player.muted);
  const inLobby = lobby.phase !== 'running' && lobby.phase !== 'starting';
  const actions = [
    {
      action: 'kick',
      label: 'Kick',
      icon: 'bi-box-arrow-right',
      disabled: !inLobby,
      title: inLobby ? '' : 'Available once the match ends',
      message: `Kicking ${targetName}...`
    },
    {
      action: muted ? 'unmute' : 'mute',
      label: muted ? 'Unmute' : 'Mute',
      icon: muted ? 'bi-volume-up-fill' : 'bi-volume-mute-fill',
      message: `${muted ? 'Unmuting' : 'Muting'} ${targetName}...`
    },
    {
      action: 'ban',
      label: 'Ban',
      icon: 'bi-slash-circle',
      danger: true,
      disabled: !inLobby,
      title: inLobby ? '' : 'Available once the match ends',
      message: `Banning ${targetName}...`
    },
    // "To Spectators" only makes sense for someone holding a slot — a spectator
    // is already there.
    ...(player.isSpectator ? [] : [{
      action: 'spectate',
      label: 'To Spectators',
      icon: 'bi-eye-fill',
      disabled: !inLobby,
      title: inLobby ? '' : 'Available once the match ends',
      message: `Moving ${targetName} to spectators...`
    }])
  ];

  for (const entry of actions) {
    const btn = document.createElement('button');
    btn.type = 'button';
    btn.className = 'lobby-player-menu-btn';
    if (entry.danger) btn.classList.add('danger');
    const icon = document.createElement('i');
    icon.className = `bi ${entry.icon}`;
    icon.setAttribute('aria-hidden', 'true');
    btn.appendChild(icon);
    btn.appendChild(document.createTextNode(entry.label));
    if (entry.title) btn.title = entry.title;
    btn.disabled = Boolean(entry.disabled) || lobbyRuntime.botActionInFlight;
    btn.addEventListener('click', (event) => {
      event.preventDefault();
      event.stopPropagation();
      runModerationAction(entry.action, targetName, entry.message);
    });
    pop.appendChild(btn);
  }

  document.body.appendChild(pop);
  const rect = chip.getBoundingClientRect();
  const popW = pop.offsetWidth || 180;
  const popH = pop.offsetHeight || 170;
  const left = clamp(rect.left + rect.width / 2 - popW / 2, 8, window.innerWidth - popW - 8);
  let top = rect.bottom + 8;
  if (top + popH > window.innerHeight - 8) top = rect.top - popH - 8;
  top = clamp(top, 8, window.innerHeight - popH - 8);
  pop.style.left = `${left}px`;
  pop.style.top = `${top}px`;

  pop._onDismiss = (event) => {
    if (pop.contains(event.target) || chip.contains(event.target)) return;
    closeLobbyPlayerMenu();
  };
  pop._onEscape = (event) => {
    if (event.key === 'Escape') closeLobbyPlayerMenu();
  };
  document.addEventListener('pointerdown', pop._onDismiss, true);
  document.addEventListener('keydown', pop._onEscape, true);

  lobbyPlayerMenuEl = pop;
}

function handleColorTaken(data) {
  if (Date.now() - lobbyColorPickSentAt > 5000) return;
  const pending = lobbyPendingColorPick;
  lobbyPendingColorPick = null;
  if (pending && pending.colorId === data?.colorId) {
    pending.player.colorId = pending.previousColorId;
    pending.player.color = pending.previousColor;
    pending.player.colorChosen = pending.previousColorChosen;
  }
  const name = typeof data?.name === 'string' && data.name.trim() ? data.name.trim() : 'Another player';
  lobby.message = `${name} already owns that color.`;
  renderLobby();
}

function handleTrailOpacityChanged(data) {
  const rawPercent = Number(data?.trailOpacityPercent);
  if (!Number.isFinite(rawPercent)) return;
  const trailOpacityPercent = clampTrailPercent(rawPercent);
  const botIndex = Number(data?.botIndex);
  const name = typeof data?.name === 'string' ? data.name.trim() : '';
  const player = (Array.isArray(lobby.players) ? lobby.players : []).find((slot) => {
    if (Number.isInteger(botIndex) && botIndex >= 0) {
      return sanitizeBotKind(slot?.botKind) === 'manual' && slot?.botIndex === botIndex;
    }
    return Boolean(name) && slot?.isClientSlot && sameHostName(slot?.name, name);
  });
  if (!player) return;
  player.trailOpacityPercent = trailOpacityPercent;
  publishSlotTrailOpacities();
  refreshLobbyPlayerChipAlpha(player);
}

function applyOwnTurnRadiusPercent(rawPercent, rawRadius = null) {
  const parsedPercent = Number(rawPercent);
  if (!Number.isFinite(parsedPercent)) return;
  const turnRadiusPercent = normalizePlayerTurnRadiusPercent(parsedPercent);
  const parsedRadius = Number(rawRadius);
  const turnRadius = Number.isFinite(parsedRadius) && parsedRadius > 0
    ? parsedRadius
    : playerTurnRadiusForPercent(turnRadiusPercent);
  net.turnRadiusPercent = turnRadiusPercent;
  net.turnRadius = turnRadius;
  model.config.turnRadius = turnRadius;
  if (settingsTurnRadiusEl) {
    settingsTurnRadiusEl.value = String(turnRadiusPercent);
  }
  if (settingsTurnRadiusValueEl) {
    settingsTurnRadiusValueEl.value = String(turnRadiusPercent);
  }
}

function handleTurnRadiusChanged(data) {
  const name = typeof data?.name === 'string' ? data.name.trim() : '';
  if (!name) return;
  const player = (Array.isArray(lobby.players) ? lobby.players : []).find((slot) => (
    slot?.isClientSlot && sameHostName(slot?.name, name)
  ));
  const parsedPercent = Number(data?.turnRadiusPercent);
  if (!Number.isFinite(parsedPercent)) return;
  const turnRadiusPercent = normalizePlayerTurnRadiusPercent(parsedPercent);
  if (player) player.turnRadiusPercent = turnRadiusPercent;
  if (sameHostName(name, net.playerName)) {
    applyOwnTurnRadiusPercent(turnRadiusPercent, data?.turnRadius);
  }
}

// Shared by the HTTP fetch that seeds the lobby and the pushed updates that
// keep it current, so a streamed room can never be shaped differently from a
// fetched one.
function applyRoomDirectory(rawRooms) {
  const rooms = Array.isArray(rawRooms) ? rawRooms : [];
  lobby.availableRooms = rooms.map((room) => ({
    roomId: sanitizeRoomId(room?.roomId || ''),
    roomName: typeof room?.name === 'string' ? room.name.trim() : '',
    hostName: typeof room?.hostName === 'string' ? room.hostName.trim() : '',
    connectedPlayers: clampRoomPlayers(room?.connectedPlayers, 0, 0, 8),
    connectedClients: clampRoomPlayers(room?.connectedClients, 0, 0, 8),
    humanPlayers: clampRoomPlayers(room?.humanPlayers, 0, 0, 8),
    botPlayers: clampRoomPlayers(room?.botPlayers, 0, 0, 8),
    manualBotCount: clampRoomPlayers(room?.manualBotCount, 0, 0, 8),
    maxPlayers: clampRoomPlayers(room?.maxPlayers, 8),
    minPlayersToStart: clampRoomPlayers(room?.minPlayersToStart, 1),
    startMode: sanitizeStartMode(room?.startMode, 'host'),
    // Teams is the one format the counts cannot express (see
    // matchFormatForCounts), so the row's format label needs the flag itself.
    teamMode: room?.teamMode === true,
    // Everything the directory knows about a room's password: that there is
    // one. The server sends no more than this.
    locked: room?.locked === true,
    phase: typeof room?.phase === 'string' ? room.phase : 'waiting'
  })).filter((room) => room.roomId.length > 0);
}

function applyPushedRoomDirectory(rawRooms) {
  // Ignored unless the room browser is actually on screen: in a room the list
  // is stale by definition and gets refetched on the way out.
  if (lobby.mode !== 'browser' || !lobby.visible) return;
  applyRoomDirectory(rawRooms);
  renderLobby();
}

async function fetchRoomDirectory() {
  const data = await authJson('/api/rooms', 'GET');
  applyRoomDirectory(data?.rooms);
}

async function createRoomViaApi(settings = {}) {
  const payload = {
    hostName: net.playerName,
    roomId: settings.roomId || '',
    roomName: settings.roomName || '',
    // Top level, not inside `settings`: settings are the shape of the match and
    // are patchable afterwards through /settings. A password is neither.
    ...(settings.password ? { password: settings.password } : {}),
    settings: {
      minPlayersToStart: clampRoomPlayers(settings.minPlayersToStart, 1),
      maxPlayers: clampRoomPlayers(settings.maxPlayers, 8),
      startMode: sanitizeStartMode(settings.startMode, 'host'),
      teamMode: Boolean(settings.teamMode)
    }
  };
  if (payload.settings.minPlayersToStart > payload.settings.maxPlayers) {
    payload.settings.minPlayersToStart = payload.settings.maxPlayers;
  }
  const data = await authJson('/api/rooms', 'POST', payload);
  return data?.room || null;
}

function hostAuth(roomId) {
  return { sessionToken: readSession(roomId)?.token || null };
}

async function updateRoomSettingsViaApi(roomId, settings = {}) {
  const sanitizedRoomId = sanitizeRoomId(roomId);
  if (!sanitizedRoomId) throw new Error('Invalid room ID');
  const currentRoomName = normalizeRoomName(lobby.roomName, lobby.roomId);
  const nextRoomName = normalizeRoomName(settings.name, currentRoomName);
  const payload = {
    actorName: net.playerName,
    settings: {
      ...(!authState.guest && nextRoomName !== currentRoomName
        ? { name: nextRoomName }
        : {}),
      minPlayersToStart: clampRoomPlayers(settings.minPlayersToStart, lobby.minPlayersToStart),
      maxPlayers: clampRoomPlayers(settings.maxPlayers, lobby.maxPlayers),
      startMode: sanitizeStartMode(settings.startMode, lobby.startMode),
      teamMode: Boolean(settings.teamMode)
    }
  };
  if (payload.settings.minPlayersToStart > payload.settings.maxPlayers) {
    payload.settings.minPlayersToStart = payload.settings.maxPlayers;
  }
  const encodedRoomId = encodeURIComponent(sanitizedRoomId);
  const data = await authJson(`/api/rooms/${encodedRoomId}/settings`, 'PATCH', payload, hostAuth(sanitizedRoomId));
  return data?.room || null;
}

async function requestRoomStartViaApi(roomId) {
  const sanitizedRoomId = sanitizeRoomId(roomId);
  if (!sanitizedRoomId) throw new Error('Invalid room ID');
  const encodedRoomId = encodeURIComponent(sanitizedRoomId);
  const data = await authJson(`/api/rooms/${encodedRoomId}/start`, 'POST', {
    actorName: net.playerName
  }, hostAuth(sanitizedRoomId));
  return data?.room || null;
}

// `slotIndex` is the slot whose own "+ Bot" was pressed, so the bot fills that
// one. The top-bar stepper passes nothing and takes whatever is free.
async function addHostBotViaApi(roomId, slotIndex = null) {
  const sanitizedRoomId = sanitizeRoomId(roomId);
  if (!sanitizedRoomId) throw new Error('Invalid room ID');
  const encodedRoomId = encodeURIComponent(sanitizedRoomId);
  const data = await authJson(`/api/rooms/${encodedRoomId}/bots/add`, 'POST', {
    actorName: net.playerName,
    ...(Number.isInteger(slotIndex) && slotIndex >= 0 ? { slotIndex } : {})
  }, hostAuth(sanitizedRoomId));
  return data?.room || null;
}

// `botIndex` is the bot whose own X was pressed. Without it the server drops
// the last bot, which is what the stepper means and never what a row means.
async function removeHostBotViaApi(roomId, botIndex = null) {
  const sanitizedRoomId = sanitizeRoomId(roomId);
  if (!sanitizedRoomId) throw new Error('Invalid room ID');
  const encodedRoomId = encodeURIComponent(sanitizedRoomId);
  const data = await authJson(`/api/rooms/${encodedRoomId}/bots/remove`, 'POST', {
    actorName: net.playerName,
    ...(Number.isInteger(botIndex) && botIndex >= 0 ? { botIndex } : {})
  }, hostAuth(sanitizedRoomId));
  return data?.room || null;
}

async function removeHostBotFromLobby(message = 'Removing host bot...', botIndex = null) {
  if (!lobby.isHost || lobbyRuntime.botActionInFlight) return;
  lobbyRuntime.botActionInFlight = true;
  lobby.message = message;
  renderLobby();
  try {
    await removeHostBotViaApi(lobby.roomId, botIndex);
  } catch (error) {
    lobby.message = `Could not remove bot: ${error?.message || 'unknown error'}`;
    renderLobby();
  } finally {
    lobbyRuntime.botActionInFlight = false;
    renderLobby();
  }
}

async function setSelfBotModeViaApi(roomId, enabled) {
  const sanitizedRoomId = sanitizeRoomId(roomId);
  if (!sanitizedRoomId) throw new Error('Invalid room ID');
  const encodedRoomId = encodeURIComponent(sanitizedRoomId);
  const data = await authJson(`/api/rooms/${encodedRoomId}/self-bot`, 'POST', {
    actorName: net.playerName,
    enabled: Boolean(enabled)
  }, hostAuth(sanitizedRoomId));
  return data?.room || null;
}

// ---------------------------------------------------------------------------
// The room password prompt. Deliberately the ONLY place a password is asked
// for: a locked row opens it, a ?room= link to a locked room lands in the lobby
// and opens it, and a server rejection reopens it. One prompt means one place
// where "already holds a valid session token, do not re-prompt" has to be
// right, instead of one per entry point.
// ---------------------------------------------------------------------------
const roomPasswordPrompt = { roomId: '', els: null };

function roomPasswordEls() {
  if (roomPasswordPrompt.els) return roomPasswordPrompt.els;
  roomPasswordPrompt.els = {
    modal: document.getElementById('room-password-modal'),
    form: document.getElementById('roomPasswordForm'),
    input: document.getElementById('roomPasswordInput'),
    label: document.getElementById('roomPasswordRoom'),
    error: document.getElementById('roomPasswordError'),
    closeBtn: document.getElementById('roomPasswordCloseBtn'),
    cancelBtn: document.getElementById('roomPasswordCancelBtn')
  };
  return roomPasswordPrompt.els;
}

function openRoomPasswordPrompt(roomId, { incorrect = false } = {}) {
  const normalizedRoom = sanitizeRoomId(roomId);
  if (!normalizedRoom) return;
  const els = roomPasswordEls();
  if (!els.modal) return;
  roomPasswordPrompt.roomId = normalizedRoom;
  const known = (Array.isArray(lobby.availableRooms) ? lobby.availableRooms : [])
    .find((entry) => sanitizeRoomId(entry?.roomId || '') === normalizedRoom);
  const displayName = known?.roomName || normalizedRoom;
  if (els.label) {
    els.label.textContent = `"${displayName}" is locked. Enter its password to join.`;
  }
  if (els.error) {
    els.error.textContent = 'That password did not work.';
    els.error.hidden = !incorrect;
  }
  // Always empty, including after a refusal: a rejected secret has no reason to
  // sit in the DOM, and the field is focused so retyping costs nothing.
  if (els.input) els.input.value = '';
  els.modal.classList.remove('hidden');
  els.modal.setAttribute('aria-hidden', 'false');
  els.input?.focus();
}

function closeRoomPasswordPrompt() {
  const els = roomPasswordEls();
  if (!els.modal) return;
  roomPasswordPrompt.roomId = '';
  // The typed secret does not stay sitting in the DOM once the prompt is done
  // with it.
  if (els.input) els.input.value = '';
  if (els.error) els.error.hidden = true;
  els.modal.classList.add('hidden');
  els.modal.setAttribute('aria-hidden', 'true');
}

function submitRoomPasswordPrompt() {
  const els = roomPasswordEls();
  const roomId = roomPasswordPrompt.roomId;
  const password = els.input?.value || '';
  if (!roomId) return;
  if (!password.trim()) {
    if (els.error) {
      els.error.textContent = 'Enter the room password.';
      els.error.hidden = false;
    }
    els.input?.focus();
    return;
  }
  closeRoomPasswordPrompt();
  void joinRoom(roomId, { password });
}

// Clicking a room in the browser. A locked one asks first -- unless a session
// token for it is already stored, which is what the server accepts on its own,
// so asking would be asking someone to prove twice what they have already
// proved. If that token turns out to be stale the server answers 'room_locked'
// and the prompt opens then; this is an optimisation of the common case, never
// the enforcement.
function enterRoom(room) {
  const roomId = sanitizeRoomId(room?.roomId || '');
  if (!roomId) return;
  if (room?.locked === true && !readSession(roomId)?.token) {
    openRoomPasswordPrompt(roomId);
    return;
  }
  void joinRoom(roomId);
}

// Same decision for a room named by id rather than picked off a row (the room
// code box, a ?room= link): whatever the directory knows about it applies.
function enterRoomById(roomId) {
  const normalizedRoom = sanitizeRoomId(roomId);
  if (!normalizedRoom) return;
  const known = (Array.isArray(lobby.availableRooms) ? lobby.availableRooms : [])
    .find((entry) => sanitizeRoomId(entry?.roomId || '') === normalizedRoom);
  enterRoom(known || { roomId: normalizedRoom });
}

async function joinRoom(roomId, options = {}) {
  const normalizedRoom = sanitizeRoomId(roomId);
  if (!normalizedRoom) return;
  const suppressLobbyOverlay = options?.suppressLobbyOverlay === true;
  const password = typeof options?.password === 'string' ? options.password : '';
  lobbyRuntime.returnToLobbyInProgress = false;
  const currentRoom = sanitizeRoomId(lobby.roomId || net.roomId || 'default');
  if (net.connected && normalizedRoom === currentRoom) {
    return;
  }

  if (net.connected || authState.startedNetworking) {
    lobbyDeps.stopTimers();
    lobbyDeps.disconnect();
    net.connected = false;
    authState.startedNetworking = false;
  }

  clearHostRoomSettingsAutosave();
  lobby.mode = 'room';
  lobby.roomId = normalizedRoom;
  lobby.message = 'Joining room...';
  try {
    const url = new URL(window.location.href);
    url.searchParams.set('room', normalizedRoom);
    window.history.replaceState({}, '', url.toString());
  } catch (_) {
    // ignore
  }
  if (suppressLobbyOverlay) {
    setLobbyVisible(false);
  } else {
    setLobbyVisible(true);
    renderLobby();
  }
  await lobbyDeps.startNetworking({
    playerNameOverride: net.playerName,
    roomIdOverride: normalizedRoom,
    suppressLobbyOverlay,
    roomPassword: password
  });
}

// `keepSeat` is for stepping out to the browser DURING a live match: it leaves
// the seat behind rather than giving it up, so you can come back to your own
// snake. Without it the trip is one-way for everyone, signed in or not --
// `bye` makes the server call removeClient(client, false), which SKIPS the
// disconnect grace, so the seat is freed the instant you leave and there is
// nothing left for either reclaim path to find. Dropping the socket without
// the bye instead looks like any other disconnect: the curve greys out, the
// seat is held for MATCH_DISCONNECT_GRACE_MS, and rejoining with the stored
// token lands back in it via adoptSession. Clearing the session is skipped for
// the same reason -- it is a guest's ONLY route back (a passkey account can
// also reclaim by name, but only while its client still exists).
async function returnToLobbyBrowser({ keepSeat = false } = {}) {
  if (!authState.authenticated) return;
  lobbyRuntime.matchLobbyPeek = false;
  lobbyRuntime.openWaitingRoomAfterJoin = false;
  lobbyRuntime.returnToLobbyInProgress = true;
  ui.controlsDirty = true;
  clearHostRoomSettingsAutosave();
  lobbyDeps.stopTimers();
  if (net.connected || authState.startedNetworking) {
    if (!keepSeat) {
      try {
        sendBye();
      } catch (_) {
      }
    }
    lobbyDeps.disconnect();
    net.connected = false;
    authState.startedNetworking = false;
  }
  if (!keepSeat) clearSession(net.roomId);
  lobbyDeps.clearInput();
  lobby.mode = 'browser';
  // disconnect() deliberately invalidates the room socket before closing it,
  // so no network "close" callback runs for this intentional transition.
  // Move chat to global immediately and retire the room channel ourselves.
  resetChatFeed('', true);
  lobbyRuntime.botActionInFlight = false;
  lobby.phase = 'waiting';
  lobby.connectedPlayers = 0;
  // Nobody's room any more: drop its presence count and authoritative style
  // map rather than carrying either into the next room's first frame.
  setRoomPresenceCount(null);
  setSlotTrailOpacities([]);
  lobby.connectedClients = 0;
  lobby.humanPlayers = 0;
  lobby.botPlayers = 0;
  lobby.manualBotCount = 0;
  lobby.players = [];
  lobby.iAmSpectator = false;
  lobby.iAmMuted = false;
  lobby.message = 'Create or join a room.';
  lobbyRuntime.createFormTouched = false;
  setLobbyVisible(true);
  if (!keepSeat) {
    try {
      const url = new URL(window.location.href);
      url.searchParams.delete('room');
      window.history.replaceState({}, '', url.toString());
    } catch (_) {
      // ignore
    }
  }
  try {
    await fetchRoomDirectory();
  } catch (_) {
    // ignore
  }
  renderLobby();
  setStatus(`Signed in as ${net.playerName}`);
  // An intentional disconnect never emits the transport 'close' event (the
  // connection token is invalidated first), so the flag is cleared here once
  // the teardown above has finished rather than in the close handler.
  lobbyRuntime.returnToLobbyInProgress = false;
  ui.controlsDirty = true;
}

function isLobbyVisible() {
  return Boolean(lobbyOverlayEl && !lobbyOverlayEl.classList.contains('hidden'));
}

// Waiting-room peek: the in-game Leave button opens the room's waiting room
// without actually leaving the room (the WS session stays connected). This
// applies any time the player is still a room member — mid-match (the spec/
// play toggle jumps back into the live view) or right after a match ends
// (deciding on the next round) — not just strictly while a match is running.
// The waiting room's own "Back" button is the one that actually leaves,
// via returnToLobbyBrowser().
function openWaitingRoomPeek() {
  if (!net.connected || lobby.mode !== 'room') return false;
  lobbyRuntime.matchLobbyPeek = true;
  lobbyDeps.clearInput();
  setLobbyVisible(true);
  renderLobby();
  return true;
}

function returnToMatchFromPeek() {
  lobbyRuntime.matchLobbyPeek = false;
  setLobbyVisible(false);
  renderLobby();
  ui.controlsDirty = true;
}

function setLobbyVisible(show) {
  lobby.visible = Boolean(show);
  if (!lobbyOverlayEl) return;
  lobbyOverlayEl.classList.toggle('hidden', !lobby.visible);
  // Hand the chat back to the game sidebar the moment the lobby closes
  // (match start), and reclaim it when the lobby opens.
  mountChatPanel(lobby.visible);
  syncLobbyChatConnection();
  syncStageVisibility();
}

// The game HUD (canvas + sidebar) stays offstage whenever a full-screen
// overlay owns the viewport; it only shows once a match is actually on.
function syncStageVisibility() {
  if (!arenaEl) return;
  const authVisible = Boolean(authOverlayEl && !authOverlayEl.classList.contains('hidden'));
  arenaEl.classList.toggle('is-offstage', lobby.visible || authVisible);
  // A match can start while the player is tabbed out, which puts the arena on
  // stage with no focus event to follow; re-check the click-to-focus band here.
  syncFocusOverlay();
}

// Global chat remains live in rooms and matches so its inactive-channel dot
// can report new messages immediately.
function syncLobbyChatConnection() {
  setLobbyChatDesired(authState.authenticated);
}

// A team room needs an even roster as well as a minimum, so "1 more player"
// has to count to the next legal size rather than to the minimum.
function teamPlayersNeeded() {
  const seated = Math.max(0, Number(lobby.connectedPlayers) || 0);
  if (seated < MIN_TEAM_PLAYERS) return MIN_TEAM_PLAYERS - seated;
  return seated % 2 === 0 ? 0 : 1;
}

function lobbyPlayersNeeded() {
  if (lobby.teamMode) return teamPlayersNeeded();
  return Math.max(0, lobby.minPlayersToStart - lobby.connectedPlayers);
}

function lobbyReadyLabel() {
  if (lobby.phase === 'starting') return 'Starting';
  if (lobby.canStart) return 'Ready';
  return 'Waiting';
}

function buildLobbyInviteUrl() {
  const roomId = (typeof lobby.roomId === 'string' && lobby.roomId.trim())
    ? lobby.roomId.trim()
    : (typeof net.roomId === 'string' && net.roomId.trim() ? net.roomId.trim() : 'default');
  try {
    const inviteUrl = new URL(window.location.href);
    inviteUrl.searchParams.set('room', roomId);
    inviteUrl.searchParams.delete('name');
    return inviteUrl.toString();
  } catch (_) {
    return window.location.href;
  }
}

function setLobbyCopyButtonLabel(label, resetMs = 0) {
  if (!lobbyCopyBtnEl) return;
  // Into the span when there is one, so the button keeps its icon.
  const labelEl = lobbyCopyBtnEl.querySelector('span') || lobbyCopyBtnEl;
  labelEl.textContent = label;
  if (lobbyRuntime.copyButtonResetHandle) {
    clearTimeout(lobbyRuntime.copyButtonResetHandle);
    lobbyRuntime.copyButtonResetHandle = 0;
  }
  if (resetMs > 0) {
    lobbyRuntime.copyButtonResetHandle = setTimeout(() => {
      lobbyRuntime.copyButtonResetHandle = 0;
      if (lobbyCopyBtnEl) {
        (lobbyCopyBtnEl.querySelector('span') || lobbyCopyBtnEl).textContent = 'Copy Invite';
      }
    }, resetMs);
  }
}

async function copyLobbyInviteLink() {
  if (!lobbyCopyBtnEl) return;
  const inviteUrl = buildLobbyInviteUrl();
  if (!navigator.clipboard?.writeText) {
    setLobbyCopyButtonLabel('Copy Unsupported', 1300);
    return;
  }
  try {
    await navigator.clipboard.writeText(inviteUrl);
    setLobbyCopyButtonLabel('Copied', 1300);
  } catch (_) {
    setLobbyCopyButtonLabel('Copy Failed', 1300);
  }
}

function setupLobbyUi() {
  // The room list is pushed over the lobby socket now; nothing polls it.
  setLobbyRoomsHandler(applyPushedRoomDirectory);
  {
    const els = roomPasswordEls();
    // A <form>, so Enter in the field submits without a keydown handler of its
    // own -- and preventDefault, or the page navigates.
    els.form?.addEventListener('submit', (event) => {
      event.preventDefault();
      submitRoomPasswordPrompt();
    });
    els.closeBtn?.addEventListener('click', () => closeRoomPasswordPrompt());
    els.cancelBtn?.addEventListener('click', () => closeRoomPasswordPrompt());
    els.modal?.addEventListener('click', (event) => {
      // Clicking the backdrop cancels, same as every other modal here; a click
      // inside the card must not.
      if (event.target === els.modal) closeRoomPasswordPrompt();
    });
    els.modal?.addEventListener('keydown', (event) => {
      if (event.key !== 'Escape') return;
      event.preventDefault();
      closeRoomPasswordPrompt();
    });
  }
  if (lobbyBackBtnEl) {
    lobbyBackBtnEl.addEventListener('click', () => {
      void returnToLobbyBrowser();
    });
  }
  if (lobbyNavLobbyBtnEl) {
    // In the browser this is the page you are on; from a waiting room it is
    // the same way out as All rooms.
    lobbyNavLobbyBtnEl.addEventListener('click', () => {
      if (lobby.mode === 'browser') return;
      void returnToLobbyBrowser();
    });
  }
  if (lobbyRoomFiltersEl) {
    lobbyRoomFiltersEl.addEventListener('click', (event) => {
      const chip = event.target.closest('.lobby-filter-chip');
      if (!chip) return;
      setRoomFilterFormat(chip.dataset.format || 'all');
    });
  }
  if (lobbyRoomSearchInputEl) {
    lobbyRoomSearchInputEl.addEventListener('input', () => {
      setRoomFilterQuery(lobbyRoomSearchInputEl.value);
    });
  }
  if (lobbySpectatorPrevBtnEl) {
    lobbySpectatorPrevBtnEl.addEventListener('click', () => {
      lobbySpectatorPage -= 1;
      renderLobbySpectators(lobby.mode === 'browser');
    });
  }
  if (lobbySpectatorNextBtnEl) {
    lobbySpectatorNextBtnEl.addEventListener('click', () => {
      lobbySpectatorPage += 1;
      renderLobbySpectators(lobby.mode === 'browser');
    });
  }
  if (lobbySpectateToggleBtnEl) {
    lobbySpectateToggleBtnEl.addEventListener('click', () => {
      if (!net.connected) return;
      // Pure spectator → ask to play; player or plays-next → ask to watch.
      if (!sendSpectate(!lobby.iAmSpectator)) {
        lobby.message = 'Could not change spectate mode while disconnected.';
        renderLobby();
      }
    });
  }
  if (lobbyResumeMatchBtnEl) {
    lobbyResumeMatchBtnEl.addEventListener('click', () => {
      if (!net.connected) return;
      returnToMatchFromPeek();
    });
  }
  if (lobbyRejoinMatchBtnEl) {
    lobbyRejoinMatchBtnEl.addEventListener('click', () => {
      const roomId = heldSeatRoomId();
      if (!roomId) return;
      // Straight back to the arena: clearing the peek flag lets the room_state
      // handler keep the arena view once the join lands.
      lobbyRuntime.matchLobbyPeek = false;
      void joinRoom(roomId);
    });
  }
  if (lobbyRejoinRoomBtnEl) {
    lobbyRejoinRoomBtnEl.addEventListener('click', () => {
      const roomId = heldSeatRoomId();
      if (!roomId) return;
      // Same rejoin, but land in the waiting room instead of the arena. Set as
      // a one-shot rather than matchLobbyPeek, which the room_state handler
      // clears while the phase is still settling after a join.
      lobbyRuntime.openWaitingRoomAfterJoin = true;
      void joinRoom(roomId);
    });
  }
  if (lobbyCopyBtnEl) {
    lobbyCopyBtnEl.addEventListener('click', () => {
      void copyLobbyInviteLink();
    });
  }
  if (lobbyCreateFormatSelectEl) {
    lobbyCreateFormatSelectEl.addEventListener('change', () => {
      applyMatchFormatToSelects(lobbyCreateFormatSelectEl, lobbyCreateMinSelectEl, lobbyCreateMaxSelectEl);
      syncCreateTeamCountOptions();
    });
    syncCreateTeamCountOptions();
  }
  const onCreateCountChange = () => {
    syncMatchFormatSelect(
      lobbyCreateFormatSelectEl,
      lobbyCreateMinSelectEl?.value,
      lobbyCreateMaxSelectEl?.value,
      lobbyCreateFormatSelectEl?.value === 'teams'
    );
  };
  // Any edit to the create row stops it being reseeded, until the player
  // leaves the browser or the room is created.
  for (const el of [
    lobbyCreateFormatSelectEl,
    lobbyCreateMinSelectEl,
    lobbyCreateMaxSelectEl,
    lobbyCreateModeSelectEl
  ]) {
    el?.addEventListener('change', () => {
      lobbyRuntime.createFormTouched = true;
    });
  }
  if (lobbyCreateMinSelectEl) {
    lobbyCreateMinSelectEl.addEventListener('change', onCreateCountChange);
  }
  if (lobbyCreateMaxSelectEl) {
    lobbyCreateMaxSelectEl.addEventListener('change', onCreateCountChange);
  }
  if (lobbyNewRoomBtnEl) {
    lobbyNewRoomBtnEl.addEventListener('click', async () => {
      // Always generated: there is no box to type an id into any more, and a
      // room is reached by its row in the list or by its ?room= link.
      const roomId = generateRoomId();
      const roomName = typeof lobbyRoomNameInputEl?.value === 'string'
        ? lobbyRoomNameInputEl.value.trim().slice(0, 48)
        : '';
      const maxPlayers = clampRoomPlayers(lobbyCreateMaxSelectEl?.value, 8);
      const minPlayersToStart = Math.min(
        maxPlayers,
        // 1 to match the FFA preset the form opens on; only reached if the
        // select is missing entirely, since it now defaults to Min 1.
        clampRoomPlayers(lobbyCreateMinSelectEl?.value, 1)
      );
      const startMode = sanitizeStartMode(lobbyCreateModeSelectEl?.value, 'host');
      // Teams is the one format the counts cannot express, so it has to travel
      // as its own flag -- without it the new room came up as FFA, being a
      // 4-to-8 room that matches no preset and so falls back to the general
      // free-for-all label.
      const teamMode = lobbyCreateFormatSelectEl?.value === 'teams';
      // Set once, here, and then held only long enough to make the creator's
      // own join -- which is a fresh join like any other and has to present it.
      // Every join after that is token-backed.
      const password = typeof lobbyRoomPasswordInputEl?.value === 'string'
        ? lobbyRoomPasswordInputEl.value
        : '';
      try {
        const createdRoom = await createRoomViaApi({
          roomId,
          roomName,
          minPlayersToStart,
          maxPlayers,
          startMode,
          teamMode,
          password
        });
        const createdRoomId = sanitizeRoomId(createdRoom?.roomId || roomId);
        if (!createdRoomId) {
          throw new Error('Room creation returned an invalid room id.');
        }
        if (lobbyRoomPasswordInputEl) lobbyRoomPasswordInputEl.value = '';
        lobbyRuntime.createFormTouched = false;
        lobby.message = 'Room created. Joining...';
        renderLobby();
        await joinRoom(createdRoomId, { password });
      } catch (error) {
        lobby.message = `Failed to create room: ${error?.message || 'unknown error'}`;
        try {
          await fetchRoomDirectory();
        } catch (_) {
          // ignore
        }
        renderLobby();
      }
    });
  }
  if (lobbyHostModeToggleEl) {
    lobbyHostModeToggleEl.addEventListener('click', (event) => {
      if (!lobby.isHost) return;
      const option = event.target.closest('.lobby-startmode-option');
      if (!option) return;
      setLobbyStartModeToggle(option.dataset.mode);
      queueHostRoomSettingsAutosave();
    });
  }
  if (lobbyHostNameInputEl) {
    lobbyHostNameInputEl.addEventListener('input', () => queueHostRoomNameAutosave());
    lobbyHostNameInputEl.addEventListener('keydown', (event) => {
      if (event.key !== 'Enter') return;
      event.preventDefault();
      lobbyHostNameInputEl.blur();
      queueHostRoomNameAutosave();
    });
  }
  if (lobbyHostFormatSelectEl) {
    lobbyHostFormatSelectEl.addEventListener('change', () => {
      if (!applyMatchFormatToSelects(lobbyHostFormatSelectEl, lobbyHostMinSelectEl, lobbyHostMaxSelectEl)) return;
      queueHostRoomSettingsAutosave();
    });
  }
  const onHostCountChange = () => {
    // Keep the team flag: it is not derivable from the counts, and the autosave
    // below reads it straight back off this select -- so re-deriving it as
    // false here did not merely mislabel the room, it turned team mode OFF the
    // moment the host touched Min or Max.
    syncMatchFormatSelect(
      lobbyHostFormatSelectEl,
      lobbyHostMinSelectEl?.value,
      lobbyHostMaxSelectEl?.value,
      lobbyHostFormatSelectEl?.value === 'teams'
    );
    queueHostRoomSettingsAutosave();
  };
  if (lobbyHostMinSelectEl) {
    lobbyHostMinSelectEl.addEventListener('change', onHostCountChange);
  }
  if (lobbyHostMaxSelectEl) {
    lobbyHostMaxSelectEl.addEventListener('change', onHostCountChange);
  }
  if (lobbyHostStartBtnEl) {
    lobbyHostStartBtnEl.addEventListener('click', async (event) => {
      markAudioInteraction(event);
      // Stopping an in-motion countdown is open to any connected room
      // member, not just the host — the server enforces the same shared
      // stop limit regardless of who presses it. Starting a fresh game
      // stays host-only.
      if (lobbyCountdownActive()) {
        sendCancelCountdown();
        return;
      }
      if (!lobby.isHost) return;
      try {
        await requestRoomStartViaApi(lobby.roomId);
      } catch (error) {
        lobby.message = `Start failed: ${error?.message || 'unknown error'}`;
        renderLobby();
      }
    });
  }
  if (lobbyHostAddBotBtnEl) {
    lobbyHostAddBotBtnEl.addEventListener('click', async () => {
      if (!lobby.isHost || lobbyRuntime.botActionInFlight) return;
      lobbyRuntime.botActionInFlight = true;
      lobby.message = lobbyWillSeatHostAsBotFromReserve()
        ? 'Joining the reserved slot as a bot...'
        : 'Adding host bot...';
      renderLobby();
      try {
        await addHostBotViaApi(lobby.roomId);
      } catch (error) {
        lobby.message = `Could not add bot: ${error?.message || 'unknown error'}`;
        renderLobby();
      } finally {
        lobbyRuntime.botActionInFlight = false;
        renderLobby();
      }
    });
  }
  if (lobbyHostRemoveBotBtnEl) {
    lobbyHostRemoveBotBtnEl.addEventListener('click', () => {
      void removeHostBotFromLobby();
    });
  }
}

function buildLobbySlotSnapshot() {
  const slotCount = clamp(Math.max(lobby.minPlayersToStart, Math.min(lobby.maxPlayers, 8)), 1, 8);
  const slots = new Array(slotCount).fill(null);
  // Somebody who left mid-match is still in lobby.players -- the score panel
  // needs their name to keep their result legible -- but they are not in the
  // room, so they do not hold a seat in it.
  const players = Array.isArray(lobby.players)
    ? lobby.players.filter((player) => player?.inRoom !== false).sort((a, b) => {
      const aSlot = Number.isFinite(Number(a?.slotIndex)) ? Number(a.slotIndex) : Number.MAX_SAFE_INTEGER;
      const bSlot = Number.isFinite(Number(b?.slotIndex)) ? Number(b.slotIndex) : Number.MAX_SAFE_INTEGER;
      if (aSlot !== bSlot) return aSlot - bSlot;
      const aId = Number.isFinite(Number(a?.playerId)) ? Number(a.playerId) : Number.MAX_SAFE_INTEGER;
      const bId = Number.isFinite(Number(b?.playerId)) ? Number(b.playerId) : Number.MAX_SAFE_INTEGER;
      return aId - bId;
    })
    : [];

  for (const player of players) {
    let slotIndex = Number(player?.slotIndex);
    if (!Number.isFinite(slotIndex) || slotIndex < 0 || slotIndex >= slotCount || slots[slotIndex]) {
      slotIndex = slots.findIndex((slot) => slot === null);
    }
    if (slotIndex < 0) continue;
    slots[slotIndex] = player;
  }

  return { slotCount, slots };
}

function lobbyWillSeatHostAsBotFromReserve() {
  return lobby.isHost
    && lobby.iAmSpectator
    && lobby.phase !== 'running'
    && lobby.phase !== 'starting'
    && lobby.humanPlayers <= 0
    && lobby.connectedPlayers < lobby.maxPlayers
    && lobby.manualBotCount >= Math.max(0, lobby.maxPlayers - 1);
}

// Which room is still holding a seat for us, if any: a stored token for a room
// the directory says is running. This is the same token the rejoin presents, so
// the badge can never promise a reclaim that would not actually happen.
const ROOM_SECTION_COLLAPSE_KEY = 'curvegen.lobbySections.v1';
const collapsedRoomSections = new Set(readCollapsedRoomSections());

// View-only narrowing of the room list: which format chip is pressed and what
// is typed in the search box. Never sent anywhere and not persisted -- a filter
// that survived a reload would hide rooms from someone who forgot setting it.
const roomFilter = { format: 'all', query: '' };

const ROOM_FORMAT_LABELS = { solo: 'Solo', duel: '1v1', ffa: 'FFA', teams: 'Teams' };

const ROOM_PHASE_LABELS = {
  running: 'Live',
  starting: 'Starting',
  ready: 'Ready',
  waiting: 'Waiting'
};

function roomFormatOf(room) {
  return matchFormatForCounts(room?.minPlayersToStart, room?.maxPlayers, room?.teamMode);
}

function roomMatchesFilter(room) {
  if (roomFilter.format !== 'all' && roomFormatOf(room) !== roomFilter.format) return false;
  const query = roomFilter.query.trim().toLowerCase();
  if (!query) return true;
  const haystack = [room?.roomName, room?.roomId, room?.hostName]
    .filter((part) => typeof part === 'string')
    .join(' ')
    .toLowerCase();
  return haystack.includes(query);
}

function setRoomFilterFormat(format) {
  roomFilter.format = Object.hasOwn(ROOM_FORMAT_LABELS, format) ? format : 'all';
  renderLobby();
}

function setRoomFilterQuery(query) {
  roomFilter.query = typeof query === 'string' ? query.slice(0, 48) : '';
  renderLobby();
}

// Filled seats first (humans, then bots), then the open ones, so a glance at
// the row reads how full the room is before any number does.
function buildRoomSeatDots(room) {
  const wrap = document.createElement('span');
  wrap.className = 'lobby-room-seats';
  const max = clampRoomPlayers(room?.maxPlayers, 8);
  const filled = Math.min(max, Math.max(0, Number(room?.connectedPlayers) || 0));
  const bots = Math.min(filled, Math.max(0, Number(room?.botPlayers) || 0));
  for (let i = 0; i < max; i += 1) {
    const dot = document.createElement('span');
    dot.className = 'lobby-room-seat';
    if (i < filled - bots) dot.classList.add('is-human');
    else if (i < filled) dot.classList.add('is-bot');
    dot.setAttribute('aria-hidden', 'true');
    wrap.appendChild(dot);
  }
  const count = document.createElement('span');
  count.className = 'lobby-room-seat-count';
  count.textContent = `${filled} / ${max}`;
  wrap.appendChild(count);
  return wrap;
}

function readCollapsedRoomSections() {
  try {
    const raw = window.localStorage.getItem(ROOM_SECTION_COLLAPSE_KEY);
    const value = raw ? JSON.parse(raw) : null;
    return Array.isArray(value) ? value.filter((key) => typeof key === 'string') : [];
  } catch (_) {
    return [];
  }
}

function persistCollapsedRoomSections() {
  try {
    window.localStorage.setItem(
      ROOM_SECTION_COLLAPSE_KEY,
      JSON.stringify([...collapsedRoomSections])
    );
  } catch (_) {
    // Storage unavailable; the collapse still works for this session.
  }
}

function toggleRoomSection(key) {
  if (collapsedRoomSections.has(key)) collapsedRoomSections.delete(key);
  else collapsedRoomSections.add(key);
  persistCollapsedRoomSections();
  // The list only rebuilds when its signature changes, and the collapse state
  // is part of that signature, so this re-render actually redraws.
  renderLobby();
}

function heldSeatRoomId() {
  const rooms = Array.isArray(lobby.availableRooms) ? lobby.availableRooms : [];
  for (const room of rooms) {
    const id = sanitizeRoomId(room?.roomId || '');
    if (!id || room?.phase !== 'running') continue;
    if (readSession(id)) return id;
  }
  return '';
}

function lobbyRoomsSignature(browserMode) {
  if (!browserMode) return 'hidden';
  const rooms = Array.isArray(lobby.availableRooms) ? lobby.availableRooms : [];
  // Held room and collapse state are part of the signature: the first decides
  // the pinning and the badge, the second decides what is drawn at all, and
  // neither shows up in the per-room fields below.
  //
  // The empty case has to carry them too. It used to return a bare 'empty',
  // which is constant -- so collapsing a section in a quiet lobby updated the
  // state and then hit a signature that had not changed, and the list was
  // skipped. It only appeared to work after a reload, which rebuilds anyway.
  const prefix = `${heldSeatRoomId()}#${[...collapsedRoomSections].sort().join(',')}#${roomFilter.format}#${roomFilter.query}#`;
  if (rooms.length === 0) return `${prefix}empty`;
  return prefix + rooms.map((room) => [
    sanitizeRoomId(room?.roomId || ''),
    (typeof room?.roomName === 'string' ? room.roomName.trim() : ''),
    Number(room?.connectedPlayers) || 0,
    Number(room?.maxPlayers) || 0,
    Number(room?.minPlayersToStart) || 0,
    sanitizeStartMode(room?.startMode, 'host'),
    Number(room?.botPlayers) || 0,
    (typeof room?.hostName === 'string' ? room.hostName.trim() : ''),
    room?.locked === true ? 'L' : '',
    room?.teamMode === true ? 'T' : '',
    (typeof room?.phase === 'string' ? room.phase.trim() : 'waiting')
  ].join('~')).join('|');
}

function lobbyPlayersSignature(browserMode, slotSnapshot) {
  if (browserMode) return 'hidden';
  const { slotCount, slots } = slotSnapshot;
  const perSlot = slots.map((player, index) => {
    if (!player) {
      return `e${index}:${index < lobby.minPlayersToStart ? 1 : 0}`;
    }
    // The side is part of what a row draws (its badge, its column, its number),
    // so a room switching format has to count as a roster change.
    const teamPart = player.team || '-';
    const ownerName = typeof player.ownerName === 'string' && player.ownerName.trim()
      ? player.ownerName.trim()
      : (typeof player.name === 'string' ? player.name.trim() : '');
    const isClientSlot = Boolean(player.isClientSlot);
    const isYou = isClientSlot && sameHostName(ownerName, net.playerName);
    const isHostClient = isClientSlot && sameHostName(ownerName, lobby.hostName);
    const isBot = Boolean(player.isBot);
    const botKind = sanitizeBotKind(player.botKind);
    const showSelfBotAction = isYou && isClientSlot && lobby.phase !== 'running' && lobby.phase !== 'starting';
    return [
      Number.isFinite(Number(player.slotIndex)) ? Number(player.slotIndex) : -1,
      Number.isFinite(Number(player.playerId)) ? Number(player.playerId) : -1,
      (typeof player.name === 'string' ? player.name.trim() : ''),
      ownerName,
      isClientSlot ? 1 : 0,
      isYou ? 1 : 0,
      isHostClient ? 1 : 0,
      isBot ? 1 : 0,
      botKind,
      Number.isInteger(player.botIndex) ? player.botIndex : -1,
      player.muted ? 1 : 0,
      showSelfBotAction ? 1 : 0,
      Number.isInteger(player.colorId) ? player.colorId : -1,
      typeof player.color === 'string' ? player.color : '',
      lobbySlotTrailPercent(player),
      teamPart
    ].join('~');
  }).join('|');
  return [
    slotCount,
    lobby.minPlayersToStart,
    lobby.maxPlayers,
    lobby.phase,
    lobby.isHost ? 1 : 0,
    lobby.connectedPlayers,
    lobbyRuntime.botActionInFlight ? 1 : 0,
    (typeof lobby.hostName === 'string' ? lobby.hostName.trim() : ''),
    (typeof net.playerName === 'string' ? net.playerName : ''),
    perSlot
  ].join('#');
}

// The chat feed and input are single live DOM nodes shared between the
// waiting room and the in-game sidebar: re-mounting (not cloning) preserves
// history, listeners, and scroll position, so conversation continues
// seamlessly when the match starts.
function mountChatPanel(inLobby) {
  if (!chatFeedEl || !chatFormEl) return;
  const target = inLobby ? lobbyChatBodyEl : sideChatPanelEl;
  if (!target || chatFeedEl.parentElement === target) return;
  target.appendChild(chatFeedEl);
  target.appendChild(chatFormEl);
  chatFeedEl.scrollTop = chatFeedEl.scrollHeight;
}

const SPECTATORS_PER_PAGE = 8;
let lobbySpectatorPage = 0;

// While the server counts down to match start, the START GAME button turns
// into the countdown readout (and a stop control while stops remain).
// Driven every frame from the main loop so the number flips on time.
let lobbyCountdownShownLabel = null;

function lobbyCountdownActive() {
  if (lobby.mode !== 'room') return false;
  if (lobby.startCountdownLocalEndsAt > 0) return true;
  // During the hold the countdown record is already gone, so keep the button
  // rendering to hold "Go!" on screen -- but only into a match that is
  // actually starting, never after a stop.
  return countdownGoHoldRemainingMs() > 0
    && (lobby.phase === 'starting' || lobby.phase === 'running');
}

function renderLobbyStartCountdown() {
  if (!lobbyHostStartBtnEl) return;
  if (!lobbyCountdownActive()) {
    if (lobbyCountdownShownLabel !== null) {
      lobbyCountdownShownLabel = null;
      lobbyHostStartBtnEl.classList.remove('counting');
      delete lobbyHostStartBtnEl.dataset.count;
      renderLobby();
    }
    return;
  }
  const remaining = lobby.startCountdownLocalEndsAt - Date.now();
  const stepMs = Math.max(250, Number(lobby.startCountdownStepMs) || 1000);
  const count = remaining <= 0 ? 0 : Math.min(9, Math.ceil(remaining / stepMs));
  const stops = Math.max(0, Number(lobby.countdownStopsLeft) || 0);
  // The host is exempt from the stop limit (rate-limited server-side instead),
  // so they can always stop; everyone else is bounded by the stops left.
  const canStop = lobby.isHost || stops > 0;
  const label = count === 0
    ? 'Go!'
    : (canStop ? `Stop · ${count}` : `Starting in ${count}`);
  if (label !== lobbyCountdownShownLabel) {
    lobbyCountdownShownLabel = label;
    const labelEl = lobbyHostStartBtnEl.querySelector('span') || lobbyHostStartBtnEl;
    setTextIfChanged(labelEl, label);
    lobbyHostStartBtnEl.classList.add('counting');
    // Published for the stylesheet: the count drives the urgency ramp and the
    // Go flash, and the step keeps the drain bar in time with the number even
    // when the server is not counting in whole seconds.
    lobbyHostStartBtnEl.dataset.count = String(count);
    lobbyHostStartBtnEl.style.setProperty('--lobby-countdown-step', `${stepMs}ms`);
    const iconEl = lobbyHostStartBtnEl.querySelector('.bi');
    if (iconEl) {
      // No icon on "Go!": it is an announcement, not an action. A play glyph
      // there reads as "press me to start" at the one moment pressing does
      // nothing (the button is already disabled at count 0).
      iconEl.hidden = count === 0;
      iconEl.classList.toggle('bi-play-fill', false);
      iconEl.classList.toggle('bi-stop-fill', count !== 0);
    }
    // Re-trigger the cue pulse.
    lobbyHostStartBtnEl.classList.remove('tick');
    void lobbyHostStartBtnEl.offsetWidth;
    lobbyHostStartBtnEl.classList.add('tick');
  }
  lobbyHostStartBtnEl.disabled = !net.connected || !canStop || count === 0 || lobby.iAmSpectator;
  lobbyHostStartBtnEl.title = count === 0
    ? 'Match starting'
    : (lobby.iAmSpectator
    ? 'Spectators cannot stop the countdown'
    : (lobby.isHost
      ? 'Stop the countdown'
      : (stops > 0
        ? `Stop the countdown (${stops} stop${stops === 1 ? '' : 's'} left)`
        : 'Stop limit reached — the match is starting')));
}

function renderLobbySpectators(browserMode) {
  if (!lobbySpectatorsEl) return;
  lobbySpectatorsEl.classList.toggle('lobby-section-hidden', browserMode);
  if (browserMode) return;

  const allSpectators = Array.isArray(lobby.spectators) ? lobby.spectators : [];
  const pageCount = Math.max(1, Math.ceil(allSpectators.length / SPECTATORS_PER_PAGE));
  lobbySpectatorPage = clamp(lobbySpectatorPage, 0, pageCount - 1);
  const spectators = allSpectators.slice(
    lobbySpectatorPage * SPECTATORS_PER_PAGE,
    (lobbySpectatorPage + 1) * SPECTATORS_PER_PAGE
  );

  if (lobbySpectatorPagerEl) {
    lobbySpectatorPagerEl.classList.toggle('lobby-section-hidden', pageCount <= 1);
    if (pageCount > 1) {
      if (lobbySpectatorPageLabelEl) {
        setTextIfChanged(lobbySpectatorPageLabelEl, `${lobbySpectatorPage + 1}/${pageCount}`);
      }
      if (lobbySpectatorPrevBtnEl) lobbySpectatorPrevBtnEl.disabled = lobbySpectatorPage <= 0;
      if (lobbySpectatorNextBtnEl) lobbySpectatorNextBtnEl.disabled = lobbySpectatorPage >= pageCount - 1;
    }
  }
  const myEntry = allSpectators.find((spec) => sameHostName(spec.name, net.playerName)) || null;
  const iAmPureSpectator = Boolean(myEntry && !myEntry.wantsToPlay);
  const matchLocked = lobby.phase === 'running' || lobby.phase === 'starting';

  if (lobbySpectatorCountEl) {
    setTextIfChanged(lobbySpectatorCountEl, allSpectators.length > 0 ? String(allSpectators.length) : '');
  }

  if (lobbySpectateToggleBtnEl) {
    const iAmRacing = matchLocked && !myEntry;
    // Purely play/watch intent now: playing (or asked to play) → offer
    // Watch; pure spectator (queued or not) → offer Play. Jumping back to a
    // live match you're peeking at is the dedicated Resume button below.
    const label = iAmPureSpectator ? 'Play' : 'Watch';
    const icon = label === 'Play' ? 'bi-play-fill' : 'bi-eye-fill';
    lobbySpectateToggleBtnEl.replaceChildren();
    const iconEl = document.createElement('i');
    iconEl.className = `bi ${icon}`;
    iconEl.setAttribute('aria-hidden', 'true');
    lobbySpectateToggleBtnEl.appendChild(iconEl);
    lobbySpectateToggleBtnEl.appendChild(document.createTextNode(` ${label}`));
    // A racing player cannot step out mid-match; everyone else can toggle.
    lobbySpectateToggleBtnEl.disabled = !net.connected || iAmRacing;
    lobbySpectateToggleBtnEl.title = iAmRacing
      ? 'You cannot switch to spectating during a running match'
      : (iAmPureSpectator ? 'Join the next match as a player' : 'Watch matches without playing');
  }

  if (lobbyResumeMatchBtnEl) {
    const peeking = lobbyRuntime.matchLobbyPeek && lobby.phase === 'running';
    lobbyResumeMatchBtnEl.classList.toggle('lobby-section-hidden', !peeking);
    lobbyResumeMatchBtnEl.disabled = !net.connected;
    lobbyResumeMatchBtnEl.title = matchLocked && !myEntry
      ? 'Back into the match'
      : 'Back to watching the match';
  }

  if (lobbySpectatorListEl) {
    lobbySpectatorListEl.replaceChildren();
    if (allSpectators.length === 0) {
      const empty = document.createElement('li');
      empty.className = 'lobby-spectator lobby-spectator-empty';
      empty.textContent = 'No spectators';
      lobbySpectatorListEl.appendChild(empty);
    }
    for (const spec of spectators) {
      const row = document.createElement('li');
      row.className = 'lobby-spectator';

      const eye = document.createElement('i');
      eye.className = 'bi bi-eye-fill lobby-spectator-eye';
      eye.setAttribute('aria-hidden', 'true');
      row.appendChild(eye);

      const specName = document.createElement('span');
      specName.className = 'lobby-spectator-name';
      specName.textContent = spec.name;
      row.appendChild(specName);

      if (sameHostName(spec.name, net.playerName)) {
        const youTag = document.createElement('span');
        youTag.className = 'lobby-spectator-tag you';
        youTag.textContent = 'YOU';
        row.appendChild(youTag);
      }
      if (sameHostName(spec.name, lobby.hostName)) {
        const hostTag = document.createElement('span');
        hostTag.className = 'lobby-spectator-tag host';
        hostTag.textContent = 'HOST';
        row.appendChild(hostTag);
      }
      if (spec.wantsToPlay) {
        const intentTag = document.createElement('span');
        intentTag.className = 'lobby-spectator-tag plays-next';
        intentTag.textContent = 'READY';
        row.appendChild(intentTag);
      }

      // The host can moderate spectators from here, same menu as slot chips.
      if (lobby.isHost && !sameHostName(spec.name, net.playerName)) {
        row.classList.add('is-manageable');
        row.title = `Manage ${spec.name}`;
        row.setAttribute('role', 'button');
        row.setAttribute('tabindex', '0');
        const specPlayer = { name: spec.name, ownerName: spec.name, muted: spec.muted, isClientSlot: true, isSpectator: true };
        row.addEventListener('click', (event) => {
          event.preventDefault();
          event.stopPropagation();
          toggleLobbyPlayerMenu(row, specPlayer);
        });
      }

      lobbySpectatorListEl.appendChild(row);
    }
  }
}

function renderLobby() {
  if (!lobbyOverlayEl) return;
  refreshLobbyColorPicker();
  const browserMode = lobby.mode === 'browser';
  // The phase flips to 'running' the moment the match starts, which is BEFORE
  // the post-"Go!" hold has elapsed. Everything the strip shows has to survive
  // that flip for the beat we are holding for, or the Go is never really seen:
  // gating the row on the phase alone hid it outright, and dropping just the
  // host settings left the row to re-flow underneath the countdown -- the
  // button slid sideways and lost 176px mid-beat, which reads as a different
  // control appearing rather than as the countdown finishing.
  const holdingGo = countdownGoHoldRemainingMs() > 0;
  const matchOwnsScreen = lobby.phase === 'running' && !holdingGo;
  const showHostSettings = !browserMode && lobby.isHost && !matchOwnsScreen;
  // Anyone (not just the host) sees the Start/Stop control while a
  // countdown is in motion, so they can use their stop if one is left.
  const showActionRow = !browserMode
    && !matchOwnsScreen
    && (lobby.isHost || lobbyCountdownActive());
  lobbyOverlayEl.classList.toggle('is-browser', browserMode);
  lobbyOverlayEl.classList.toggle('is-room', !browserMode);
  lobbyOverlayEl.classList.toggle('has-host-controls', showHostSettings);
  if (lobbyTitleEl) {
    const roomTitle = (typeof lobby.roomName === 'string' && lobby.roomName.trim())
      ? lobby.roomName.trim()
      : (typeof lobby.roomId === 'string' && lobby.roomId ? lobby.roomId : 'Waiting Room');
    setTextIfChanged(lobbyTitleEl, browserMode ? 'Lobby' : roomTitle);
  }

  if (lobbyRoomPillEl) {
    const roomCountLabel = `${lobby.availableRooms.length} room${lobby.availableRooms.length === 1 ? '' : 's'}`;
    // In the rail's browser state the pill is a badge on the Lobby item, so it
    // carries the bare number and says the rest in its title.
    const roomLabel = browserMode
      ? String(lobby.availableRooms.length)
      : (
        (typeof lobby.roomName === 'string' && lobby.roomName.trim())
          ? lobby.roomName.trim()
          : (typeof lobby.roomId === 'string' && lobby.roomId ? lobby.roomId : 'default')
      );
    setTextIfChanged(lobbyRoomPillLabelEl || lobbyRoomPillEl, roomLabel);
    lobbyRoomPillEl.classList.toggle('room-count', browserMode);
    lobbyRoomPillEl.title = browserMode ? roomCountLabel : 'The room you are in';
    if (browserMode) lobbyRoomPillEl.removeAttribute('aria-current');
    else lobbyRoomPillEl.setAttribute('aria-current', 'location');
  }
  if (lobbyNavLobbyBtnEl) {
    // Lobby is where you are in the browser; from a waiting room it is the way
    // back to it (the same as the header's All rooms button).
    if (browserMode) lobbyNavLobbyBtnEl.setAttribute('aria-current', 'page');
    else lobbyNavLobbyBtnEl.removeAttribute('aria-current');
    lobbyNavLobbyBtnEl.title = browserMode ? 'Room browser' : 'Back to all rooms';
  }

  if (lobbyIdentityNameEl) {
    setTextIfChanged(lobbyIdentityNameEl, net.playerName || 'Player');
  }
  if (lobbyIdentityAvatarEl) {
    const initial = Array.from((net.playerName || 'Player').trim())[0] || 'P';
    setTextIfChanged(lobbyIdentityAvatarEl, initial.toUpperCase());
  }
  if (lobbyIdentityTagEl) {
    lobbyIdentityTagEl.hidden = !authState.guest;
  }

  if (lobbySubtitleEl) {
    setTextIfChanged(lobbySubtitleEl, browserMode
      ? (lobby.message || 'Create a room or click one below to join.')
      : (lobby.message || 'Waiting for players...'));
  }

  if (lobbyCountEl) {
    setTextIfChanged(lobbyCountEl, browserMode
      ? `${lobby.availableRooms.length} room${lobby.availableRooms.length === 1 ? '' : 's'}`
      : `${lobby.connectedPlayers} / ${lobby.maxPlayers}`);
  }
  if (lobbyCountLabelEl) {
    setTextIfChanged(lobbyCountLabelEl, browserMode ? 'Rooms' : 'Players');
  }

  // Hide the whole top command bar (stats + action row) in browser mode so
  // it leaves no empty grid row behind.
  const lobbyTopbarEl = lobbyCountEl?.closest('.lobby-topbar');
  if (lobbyTopbarEl) {
    lobbyTopbarEl.classList.toggle('lobby-section-hidden', browserMode);
  }

  if (lobbyStartRuleEl) {
    const startModeLabel = lobby.startMode === 'auto' ? 'Auto start' : 'Host start';
    setTextIfChanged(lobbyStartRuleEl, browserMode
      ? 'Browse Rooms'
      : `${lobby.minPlayersToStart} to start • ${startModeLabel}`);
  }
  if (lobbyStartRuleLabelEl) {
    setTextIfChanged(lobbyStartRuleLabelEl, browserMode ? 'Mode' : 'Start Rule');
  }

  if (lobbyReadyEl) {
    const readyText = browserMode ? 'Browsing' : lobbyReadyLabel();
    setTextIfChanged(lobbyReadyEl, readyText);
    lobbyReadyEl.classList.toggle('browsing', browserMode);
    lobbyReadyEl.classList.toggle('ready', !browserMode && lobby.canStart && lobby.phase !== 'waiting');
    lobbyReadyEl.classList.toggle('starting', !browserMode && lobby.phase === 'starting');
  }

  if (lobbyProgressFillEl) {
    const neededForStart = Math.max(1, lobby.minPlayersToStart);
    const progressRatio = clamp(lobby.connectedPlayers / neededForStart, 0, 1);
    const fillRatio = browserMode ? 1 : progressRatio;
    lobbyProgressFillEl.style.transform = `scaleX(${fillRatio.toFixed(3)})`;
    const progressWrap = lobbyProgressFillEl.parentElement;
    if (progressWrap) {
      progressWrap.classList.toggle('lobby-section-hidden', browserMode);
    }
  }

  if (lobbyHintEl) {
    const needed = lobbyPlayersNeeded();
    let hintText = '';
    if (browserMode) {
      hintText = 'Rooms persist while active. Hosts can configure settings before starting.';
    } else if (needed > 0) {
      hintText = lobby.isHost
        ? `Need ${needed} more player${needed === 1 ? '' : 's'}. Invite players or add bots to fill slots.`
        : `Need ${needed} more player${needed === 1 ? '' : 's'}. Share the invite to fill slots.`;
    } else if (lobby.phase === 'starting') {
      hintText = 'Syncing all players. Controls unlock at match start.';
    } else if (lobby.canStart) {
      hintText = lobby.startMode === 'host'
        ? 'Room is ready. Host can start when ready.'
        : 'Room ready. Match will start automatically.';
    } else {
      hintText = 'Waiting for match start...';
    }
    setTextIfChanged(lobbyHintEl, hintText);
  }

  // The two ways back, shown in the browser only while a live match is still
  // holding a seat for us. Hidden the moment it is not: an offer to rejoin a
  // match that has ended, or whose grace window has lapsed, would be a button
  // that silently does the wrong thing.
  const heldRoomIdForHeader = browserMode ? heldSeatRoomId() : '';
  if (lobbyReturnGroupEl) {
    lobbyReturnGroupEl.classList.toggle('lobby-section-hidden', !heldRoomIdForHeader);
  }
  if (lobbyCopyBtnEl) {
    lobbyCopyBtnEl.classList.toggle('lobby-section-hidden', browserMode);
  }
  if (lobbyBackBtnEl) {
    lobbyBackBtnEl.classList.toggle('lobby-section-hidden', browserMode);
  }
  if (lobbyRoomActionsEl) {
    lobbyRoomActionsEl.classList.toggle('lobby-section-hidden', !browserMode);
  }
  // The create row is SEEDED from the room you were last in, and then left
  // alone. renderLobby runs on every pushed room-directory update -- somebody
  // else joining any room anywhere -- so writing these unconditionally silently
  // reverted whatever the player had just picked, between picking it and
  // pressing New Room. The focus guard that used to be here only covered the
  // control still being looked at; the reversion happened after the blur.
  const seedCreateRow = browserMode && !lobbyRuntime.createFormTouched;
  if (lobbyCreateMinSelectEl) {
    lobbyCreateMinSelectEl.classList.toggle('lobby-section-hidden', !browserMode);
    if (seedCreateRow) {
      lobbyCreateMinSelectEl.value = String(clampRoomPlayers(lobby.minPlayersToStart, 1));
    }
  }
  if (lobbyCreateMaxSelectEl) {
    lobbyCreateMaxSelectEl.classList.toggle('lobby-section-hidden', !browserMode);
    if (seedCreateRow) {
      lobbyCreateMaxSelectEl.value = String(clampRoomPlayers(lobby.maxPlayers, 8));
    }
  }
  if (lobbyCreateFormatSelectEl) {
    lobbyCreateFormatSelectEl.classList.toggle('lobby-section-hidden', !browserMode);
    if (seedCreateRow) {
      // After the two count selects above, so it reflects what they now hold --
      // and WITH the team flag, which no count can express: seeded from the room
      // you just left, a 4-to-8 team room otherwise came back as FFA.
      syncMatchFormatSelect(
        lobbyCreateFormatSelectEl,
        lobbyCreateMinSelectEl?.value,
        lobbyCreateMaxSelectEl?.value,
        lobby.teamMode
      );
    }
    syncCreateTeamCountOptions();
  }
  if (lobbyCreateModeSelectEl) {
    lobbyCreateModeSelectEl.classList.toggle('lobby-section-hidden', !browserMode);
    if (seedCreateRow) {
      lobbyCreateModeSelectEl.value = sanitizeStartMode(lobby.startMode, 'host');
    }
  }
  if (lobbyNewRoomBtnEl) {
    lobbyNewRoomBtnEl.classList.toggle('lobby-section-hidden', !browserMode);
  }

  if (lobbyRoomFiltersEl) {
    lobbyRoomFiltersEl.classList.toggle('lobby-section-hidden', !browserMode);
    if (browserMode) {
      const rooms = Array.isArray(lobby.availableRooms) ? lobby.availableRooms : [];
      for (const chip of lobbyRoomFiltersEl.querySelectorAll('.lobby-filter-chip')) {
        const format = chip.dataset.format || 'all';
        const active = format === roomFilter.format;
        chip.classList.toggle('is-active', active);
        chip.setAttribute('aria-pressed', active ? 'true' : 'false');
        const countEl = chip.querySelector('.lobby-filter-count');
        if (countEl) {
          const count = format === 'all'
            ? rooms.length
            : rooms.filter((room) => roomFormatOf(room) === format).length;
          setTextIfChanged(countEl, String(count));
        }
      }
    }
  }

  if (lobbyRoomListEl) {
    lobbyRoomListEl.classList.toggle('lobby-section-hidden', !browserMode);
    const nextRoomsSig = lobbyRoomsSignature(browserMode);
    if (!browserMode) {
      ui.lobbyRoomsSig = nextRoomsSig;
    } else if (ui.lobbyRoomsSig !== nextRoomsSig) {
      ui.lobbyRoomsSig = nextRoomsSig;
      lobbyRoomListEl.replaceChildren();
      const allRooms = Array.isArray(lobby.availableRooms) ? lobby.availableRooms : [];
      const rooms = allRooms.filter(roomMatchesFilter);
      const filtering = rooms.length !== allRooms.length;
      {
        // Sectioned rather than one long list, because the three kinds of room
        // are three different questions: the match still holding your seat is
        // somewhere to RETURN to, a live match is somewhere to WATCH or drop
        // into, and an open room is somewhere to WAIT. Sorting alone left all
        // three reading as one column of near-identical cards.
        //
        // Empty sections are omitted entirely -- a heading over nothing is
        // just furniture, and the common case (one or two rooms) should not
        // pay for structure it does not need.
        const heldRoomId = heldSeatRoomId();
        const isHeld = (room) => Boolean(
          heldRoomId && sanitizeRoomId(room?.roomId || '') === heldRoomId
        );
        const sections = [
          {
            key: 'held',
            label: 'Your Match',
            icon: 'bi-play-fill',
            note: '',
            rooms: rooms.filter((room) => isHeld(room))
          },
          {
            key: 'live',
            label: 'Live Now',
            icon: 'bi-record-fill',
            note: '',
            standing: true,
            empty: filtering ? 'No live matches match this filter.' : 'No matches in progress right now.',
            rooms: rooms.filter((room) => !isHeld(room) && room?.phase === 'running')
          },
          {
            key: 'open',
            label: 'Open Rooms',
            icon: 'bi-door-open',
            note: '',
            standing: true,
            empty: filtering ? 'No open rooms match this filter.' : 'Create a room to start the first game.',
            rooms: rooms.filter((room) => !isHeld(room) && room?.phase !== 'running')
          }
        ];

        for (const section of sections) {
          // Your Match is conditional -- a heading for a seat you do not hold
          // is meaningless. The other two stand whether or not they have
          // anything in them, so the shape of the lobby is the same on your
          // first visit as on a busy night, and "nothing live" is stated
          // rather than left as an absence.
          if (section.rooms.length === 0 && !section.standing) continue;
          const collapsed = collapsedRoomSections.has(section.key);
          const head = document.createElement('li');
          // Presentational: the rooms are the list, these only label them, so
          // they must not read as items to anyone navigating by list. The
          // button inside is the real control.
          head.setAttribute('role', 'presentation');
          head.className = 'lobby-room-section-row';
          const toggle = document.createElement('button');
          toggle.type = 'button';
          toggle.className = `lobby-room-section is-${section.key}`;
          toggle.setAttribute('aria-expanded', collapsed ? 'false' : 'true');
          toggle.title = collapsed ? `Show ${section.label}` : `Hide ${section.label}`;
          toggle.addEventListener('click', () => toggleRoomSection(section.key));
          const caret = document.createElement('i');
          caret.className = 'bi bi-chevron-right lobby-room-section-caret';
          caret.setAttribute('aria-hidden', 'true');
          toggle.appendChild(caret);
          const headIcon = document.createElement('i');
          headIcon.className = `bi ${section.icon} lobby-room-section-icon`;
          headIcon.setAttribute('aria-hidden', 'true');
          const headLabel = document.createElement('span');
          headLabel.className = 'lobby-room-section-label';
          headLabel.textContent = section.label;
          const headCount = document.createElement('span');
          headCount.className = 'lobby-room-section-count';
          headCount.textContent = String(section.rooms.length);
          toggle.appendChild(headIcon);
          toggle.appendChild(headLabel);
          toggle.appendChild(headCount);
          if (section.note) {
            const headNote = document.createElement('span');
            headNote.className = 'lobby-room-section-note';
            headNote.textContent = section.note;
            toggle.appendChild(headNote);
          }
          const rule = document.createElement('span');
          rule.className = 'lobby-room-section-rule';
          rule.setAttribute('aria-hidden', 'true');
          toggle.appendChild(rule);
          head.appendChild(toggle);
          lobbyRoomListEl.appendChild(head);

          if (collapsed) continue;

          if (section.rooms.length === 0) {
            const blank = document.createElement('li');
            blank.className = 'lobby-room-section-empty';
            blank.textContent = section.empty;
            lobbyRoomListEl.appendChild(blank);
            continue;
          }

          for (const room of section.rooms) {
            const row = document.createElement('li');
            row.className = 'lobby-room-entry';
            // Phase on the row, not just the status cell: a live match and a
            // room still waiting are different things to a player scanning the
            // list, and one small label is not enough to tell them apart.
            row.classList.add(room?.phase === 'running' ? 'is-running' : 'is-waiting');
            row.tabIndex = 0;
            row.setAttribute('role', 'button');

            const displayName = room.roomName || room.roomId;
            const nameCell = document.createElement('span');
            nameCell.className = 'lobby-room-cell lobby-room-cell-name';
            const title = document.createElement('span');
            title.className = 'lobby-room-entry-title';
            title.textContent = displayName;
            nameCell.appendChild(title);
            // The whole of what the browser knows about the room's password:
            // that there is one. Beside the name, so a player can see which
            // rows will ask for a password before clicking.
            if (room.locked) {
              const lock = document.createElement('span');
              lock.className = 'lobby-room-entry-lock';
              lock.title = 'Password required';
              const lockIcon = document.createElement('i');
              lockIcon.className = 'bi bi-lock-fill';
              lockIcon.setAttribute('aria-hidden', 'true');
              lock.appendChild(lockIcon);
              const lockText = document.createElement('span');
              lockText.className = 'lobby-sr-only';
              lockText.textContent = 'Password required';
              lock.appendChild(lockText);
              nameCell.appendChild(lock);
            }
            const sub = document.createElement('span');
            sub.className = 'lobby-room-entry-sub';
            const roomIdChip = document.createElement('span');
            roomIdChip.className = 'lobby-room-entry-id-chip';
            roomIdChip.textContent = room.roomId || '';
            sub.appendChild(roomIdChip);
            if (room.hostName) {
              sub.appendChild(document.createTextNode(`host ${room.hostName}`));
            }
            nameCell.appendChild(sub);

            const formatCell = document.createElement('span');
            formatCell.className = 'lobby-room-cell lobby-room-cell-format';
            const format = roomFormatOf(room);
            formatCell.dataset.format = format;
            formatCell.textContent = ROOM_FORMAT_LABELS[format] || 'FFA';

            const playersCell = document.createElement('span');
            playersCell.className = 'lobby-room-cell lobby-room-cell-players';
            playersCell.appendChild(buildRoomSeatDots(room));

            const startCell = document.createElement('span');
            startCell.className = 'lobby-room-cell lobby-room-cell-start';
            startCell.textContent = `${room.startMode === 'auto' ? 'Auto' : 'Host'} · min ${room.minPlayersToStart}`;

            const phase = typeof room.phase === 'string' && ROOM_PHASE_LABELS[room.phase] ? room.phase : 'waiting';
            const statusCell = document.createElement('span');
            statusCell.className = `lobby-room-cell lobby-room-cell-status is-${phase}`;
            const statusDot = document.createElement('span');
            statusDot.className = 'lobby-room-status-dot';
            statusDot.setAttribute('aria-hidden', 'true');
            statusCell.appendChild(statusDot);
            statusCell.appendChild(document.createTextNode(ROOM_PHASE_LABELS[phase]));

            // What clicking the row does, worded as the action. It is the
            // row's label, not a second control: the row is the button.
            const pill = document.createElement('span');
            pill.className = 'lobby-room-entry-pill';
            // A seat you are still holding in a live match is not the same thing
            // as "some room is running": clicking here takes your snake back, and
            // the row is the only way back once you have stepped out to the
            // browser. Keyed on a stored session token for THIS room, which is
            // exactly what the rejoin will present to reclaim the seat.
            const holdsSeatHere = Boolean(
              heldRoomId && sanitizeRoomId(room.roomId || '') === heldRoomId
            );
            const roomFull = (Number(room.connectedPlayers) || 0) >= (Number(room.maxPlayers) || 0);
            if (holdsSeatHere) {
              pill.classList.add('is-resume');
              pill.textContent = 'Back to match';
              row.classList.add('is-resume');
              row.title = 'Your seat is being held here';
            } else if (room.phase === 'running' || roomFull) {
              // A full room seats a joiner as a spectator, so it is offered as
              // watching rather than as a seat that is not there.
              pill.classList.add('is-watch');
              pill.textContent = 'Watch';
            } else {
              pill.classList.add('is-join');
              pill.textContent = 'Join';
            }
            row.setAttribute('aria-label', `${pill.textContent}: ${displayName}, ${formatCell.textContent}, ${room.connectedPlayers} of ${room.maxPlayers} players, ${ROOM_PHASE_LABELS[phase]}${room.locked ? ', password required' : ''}`);

            row.append(nameCell, formatCell, playersCell, startCell, statusCell, pill);
            row.addEventListener('click', () => {
              enterRoom(room);
            });
            row.addEventListener('keydown', (event) => {
              if (event.key !== 'Enter' && event.key !== ' ') return;
              event.preventDefault();
              enterRoom(room);
            });
            lobbyRoomListEl.appendChild(row);
          }
        }
      }
    }
  }

  // 'running' is reachable here now that the strip survives into the Go hold:
  // the settings are on screen for that beat and must not come back to life
  // just because the match already started.
  const controlsLocked = lobby.phase === 'starting' || lobby.phase === 'running';

  // Host settings, bots, and Start/Stop live in the same top command bar as
  // the room stats, so the entire setup flow reads as one compact strip.
  {
    // Start/Stop sits in the header and is shown whenever the row used to be:
    // to the host, and to anyone while a countdown runs. The card below the
    // roster holds only the host's settings and bot stepper, so a non-host
    // watching a countdown does not get an empty card.
    if (lobbyHostStartGroupEl) {
      lobbyHostStartGroupEl.classList.toggle('lobby-section-hidden', !showActionRow);
    }
    if (lobbyActionRowEl) {
      lobbyActionRowEl.classList.toggle('lobby-section-hidden', !(showActionRow && lobby.isHost));
    }
    if (lobbyHostSettingsGroupEl) {
      lobbyHostSettingsGroupEl.classList.toggle('lobby-section-hidden', !showHostSettings);
      if (showHostSettings) {
        applyHostRoomSettingsControls();
        setHostRoomSettingsControlsDisabled(controlsLocked);
      }
    }
    // The bot stepper is host-only tooling; a non-host peeking at someone
    // else's countdown only gets the Start/Stop control next to it.
    if (lobbyHostBotsGroupEl) {
      lobbyHostBotsGroupEl.classList.toggle('lobby-section-hidden', !(showActionRow && lobby.isHost));
    }
    if (showActionRow && lobby.isHost) {
      const roomHasCapacity = lobby.connectedPlayers < lobby.maxPlayers;
      const reservedBotSlot = lobbyWillSeatHostAsBotFromReserve();
      if (lobbyHostAddBotBtnEl) {
        lobbyHostAddBotBtnEl.disabled = lobbyRuntime.botActionInFlight || controlsLocked || !roomHasCapacity;
        lobbyHostAddBotBtnEl.title = reservedBotSlot ? 'Join reserved slot as a bot' : 'Add bot';
        lobbyHostAddBotBtnEl.setAttribute('aria-label', reservedBotSlot ? 'Join reserved slot as a bot' : 'Add bot');
      }
      if (lobbyHostRemoveBotBtnEl) {
        lobbyHostRemoveBotBtnEl.disabled = lobbyRuntime.botActionInFlight
          || controlsLocked
          || lobby.manualBotCount <= 0;
      }
      if (lobbyHostBotCountEl) {
        setTextIfChanged(
          lobbyHostBotCountEl,
          `${lobby.manualBotCount} host bot${lobby.manualBotCount === 1 ? '' : 's'}`
        );
      }
    }
    if (showActionRow) {
      if (lobbyHostStartBtnEl && !lobbyCountdownActive()) {
        const label = lobbyHostStartBtnEl.querySelector('span') || lobbyHostStartBtnEl;
        setTextIfChanged(label, 'Start Game');
        lobbyHostStartBtnEl.classList.remove('counting');
        delete lobbyHostStartBtnEl.dataset.count;
        lobbyHostStartBtnEl.title = '';
        const iconEl = lobbyHostStartBtnEl.querySelector('.bi');
        if (iconEl) {
          iconEl.classList.add('bi-play-fill');
          iconEl.classList.remove('bi-stop-fill');
        }
        lobbyHostStartBtnEl.disabled = !lobby.canStart
          || controlsLocked
          || lobby.phase === 'running'
          || lobbyRuntime.botActionInFlight;
      }
    }
  }

  if (lobbyChatEl) {
    lobbyChatEl.classList.remove('lobby-section-hidden');
    mountChatPanel(isLobbyVisible());
  }
  syncLobbyChatConnection();

  renderLobbySpectators(browserMode);

  if (!lobbyPlayerListEl) return;
  lobbyPlayerListEl.classList.toggle('lobby-section-hidden', browserMode);
  if (browserMode) {
    ui.lobbyPlayersSig = 'hidden';
    return;
  }
  const slotSnapshot = buildLobbySlotSnapshot();
  const nextPlayersSig = lobbyPlayersSignature(browserMode, slotSnapshot);
  if (ui.lobbyPlayersSig === nextPlayersSig) return;
  ui.lobbyPlayersSig = nextPlayersSig;
  lobbyPlayerListEl.replaceChildren();
  const { slotCount, slots } = slotSnapshot;
  const colorIds = buildLobbyColorIds(slots);
  // Column-major fill: with 2 columns the grid should read 1,2,3,4 down the
  // left and 5,6,7,8 down the right, so pin the row count to half the slots.
  lobbyPlayerListEl.style.setProperty('--lobby-slot-rows', String(Math.ceil(slotCount / 2)));

  // In team mode the two columns ARE the two sides. Seats alternate, so
  // rendering every even seat and then every odd one puts Fire down the left
  // and Water down the right without the grid having to know about teams --
  // and each side is then numbered 1-4 in its own column, because "player 7 of
  // Fire" is not a thing anybody can act on.
  const order = lobby.teamMode
    ? [
      ...Array.from({ length: slotCount }, (_, i) => i).filter((i) => i % 2 === 0),
      ...Array.from({ length: slotCount }, (_, i) => i).filter((i) => i % 2 === 1)
    ]
    : Array.from({ length: slotCount }, (_, i) => i);
  const perTeamSlots = Math.ceil(slotCount / 2);
  if (lobby.teamMode) {
    // One extra row per column for the title above each side.
    lobbyPlayerListEl.style.setProperty('--lobby-slot-rows', String(perTeamSlots + 1));
  }

  for (const [position, i] of order.entries()) {
    if (lobby.teamMode && position % perTeamSlots === 0) {
      lobbyPlayerListEl.appendChild(buildLobbyTeamHead(i % 2 === 0 ? TEAM_FIRE : TEAM_WATER, slots));
    }
    const seatNumber = lobby.teamMode
      ? Math.floor(position % Math.ceil(slotCount / 2)) + 1
      : i + 1;
    const player = slots[i];
    const row = document.createElement('li');
    row.className = 'lobby-player';
    if (player) {
      applyLobbyPlayerColor(row, player.color || playerColorById(colorIds[i]), player);
    }

    const chip = document.createElement('span');
    chip.className = 'lobby-player-chip';
    chip.textContent = String(seatNumber);
    row.appendChild(chip);

    const name = document.createElement('span');
    name.className = 'lobby-player-name';
    const you = document.createElement('span');
    you.className = 'lobby-player-you';
    const action = document.createElement('span');
    action.className = 'lobby-player-action';

    if (player) {
      const ownerName = typeof player.ownerName === 'string' && player.ownerName.trim()
        ? player.ownerName.trim()
        : (typeof player.name === 'string' ? player.name.trim() : '');
      const isClientSlot = Boolean(player.isClientSlot);
      const isYou = isClientSlot && sameHostName(ownerName, net.playerName);
      const isHostClient = isClientSlot && sameHostName(ownerName, lobby.hostName);
      const isBot = Boolean(player.isBot);
      const botKind = sanitizeBotKind(player.botKind);
      const canRemoveManualBot = lobby.isHost
        && isBot
        && botKind === 'manual'
        && lobby.phase !== 'running'
        && lobby.phase !== 'starting';
      if (isBot) {
        row.classList.add('lobby-player-bot');
        chip.classList.add('bot');
        const botIcon = document.createElement('i');
        botIcon.className = 'bi bi-robot lobby-player-name-icon';
        botIcon.setAttribute('aria-hidden', 'true');
        name.appendChild(botIcon);
      }
      const nameText = document.createElement('span');
      nameText.className = 'lobby-player-name-text';
      nameText.textContent = player.name || `Player ${i + 1}`;
      name.appendChild(nameText);
      if (isHostClient) {
        const hostTag = document.createElement('span');
        hostTag.className = 'lobby-player-host-tag';
        hostTag.textContent = 'HOST';
        name.appendChild(hostTag);
      }
      // The side, on the seat that decides it. Shown before YOU/HOST BOT
      // because which team you are on outranks what kind of player you are.
      const team = normalizeTeam(player.team);
      if (team) {
        const teamTag = document.createElement('span');
        teamTag.className = 'lobby-player-team';
        teamTag.dataset.team = team;
        const teamIcon = document.createElement('i');
        teamIcon.className = `bi ${teamIconClass(team)}`;
        teamIcon.setAttribute('aria-hidden', 'true');
        teamTag.appendChild(teamIcon);
        teamTag.appendChild(document.createTextNode(teamShortLabel(team)));
        name.appendChild(teamTag);
      }
      if (isYou && isBot) {
        you.textContent = 'YOU + BOT';
      } else if (isYou) {
        you.textContent = 'YOU';
      } else if (isBot) {
        you.textContent = botKind === 'manual' ? 'HOST BOT' : 'PLAYER BOT';
      } else {
        you.textContent = '';
      }

      const inLobbyPhase = lobby.phase !== 'running' && lobby.phase !== 'starting';
      const canPaintOwn = isYou && isClientSlot && inLobbyPhase;
      const canPaintBot = lobby.isHost && botKind === 'manual' && Number.isInteger(player.botIndex) && inLobbyPhase;
      const canManage = lobby.isHost && isClientSlot && !isYou;

      if (player.muted) {
        const muteIcon = document.createElement('i');
        muteIcon.className = 'bi bi-volume-mute-fill lobby-player-muted-icon';
        muteIcon.title = 'Muted by the host';
        muteIcon.setAttribute('aria-hidden', 'true');
        name.appendChild(muteIcon);
      }

      if (canPaintOwn || canPaintBot || canManage) {
        const openMenu = canPaintOwn || canPaintBot
          ? () => toggleLobbyColorPicker(chip, player)
          : () => toggleLobbyPlayerMenu(chip, player);
        chip.classList.add('is-paintable');
        chip.title = canPaintOwn
          ? 'Change your color'
          : (canPaintBot ? `Change ${player.name}'s color` : `Manage ${player.name}`);
        chip.setAttribute('role', 'button');
        chip.setAttribute('tabindex', '0');
        chip.addEventListener('click', (event) => {
          event.preventDefault();
          event.stopPropagation();
          openMenu();
        });
        chip.addEventListener('keydown', (event) => {
          if (event.key !== 'Enter' && event.key !== ' ') return;
          event.preventDefault();
          openMenu();
        });
      }

      if (isYou && isClientSlot && lobby.phase !== 'running' && lobby.phase !== 'starting') {
        const actionBtn = document.createElement('button');
        const enableBot = !isBot;
        // Guests play their own slot (the server enforces it too). Only the
        // bot-ON direction is barred, so a guest who is already bot-driven can
        // still take control back; DEV_MODE auto-guests keep the button, which
        // is the same carve-out the server makes.
        const barredForGuest = enableBot
          && Boolean(authState.guest)
          && typeof window.DEV_AUTO_GUEST_NAME !== 'string';
        actionBtn.type = 'button';
        actionBtn.className = 'lobby-player-action-btn';
        actionBtn.textContent = enableBot ? 'Use Bot' : 'Take Control';
        actionBtn.disabled = lobbyRuntime.botActionInFlight || barredForGuest;
        if (barredForGuest) {
          actionBtn.title = 'Sign in with a passkey to hand your slot to a bot';
        }
        actionBtn.addEventListener('click', async (event) => {
          event.preventDefault();
          event.stopPropagation();
          if (lobbyRuntime.botActionInFlight) return;
          lobbyRuntime.botActionInFlight = true;
          lobby.message = enableBot ? 'Switching your slot to bot...' : 'Switching back to player control...';
          renderLobby();
          try {
            await setSelfBotModeViaApi(lobby.roomId, enableBot);
          } catch (error) {
            lobby.message = `Could not update your slot: ${error?.message || 'unknown error'}`;
            renderLobby();
          } finally {
            lobbyRuntime.botActionInFlight = false;
            renderLobby();
          }
        });
        action.appendChild(actionBtn);
      }

      if (canRemoveManualBot) {
        const removeBtn = document.createElement('button');
        const removeLabel = `Remove ${player.name || `Bot ${i + 1}`}`;
        removeBtn.type = 'button';
        removeBtn.className = 'lobby-player-action-btn lobby-player-remove-btn';
        removeBtn.title = removeLabel;
        removeBtn.setAttribute('aria-label', removeLabel);
        removeBtn.disabled = lobbyRuntime.botActionInFlight;
        const icon = document.createElement('i');
        icon.className = 'bi bi-x-lg';
        icon.setAttribute('aria-hidden', 'true');
        removeBtn.appendChild(icon);
        const removedBotIndex = Number.isInteger(player.botIndex) ? player.botIndex : null;
        removeBtn.addEventListener('click', (event) => {
          event.preventDefault();
          event.stopPropagation();
          void removeHostBotFromLobby(`Removing ${player.name || 'host bot'}...`, removedBotIndex);
        });
        action.appendChild(removeBtn);
      }
    } else {
      row.classList.add('lobby-player-slot-empty');
      chip.classList.add('empty');
      name.classList.add('empty');
      you.classList.add('empty');
      name.textContent = i < lobby.minPlayersToStart ? 'Open Slot' : 'Optional Slot';
      you.textContent = i < lobby.minPlayersToStart ? 'Needed' : 'Open';

      const canFillSlot = lobby.isHost
        && lobby.phase !== 'running'
        && lobby.phase !== 'starting'
        && lobby.connectedPlayers < lobby.maxPlayers;
      if (canFillSlot) {
        const reservedBotSlot = lobbyWillSeatHostAsBotFromReserve();
        const fillBtn = document.createElement('button');
        fillBtn.type = 'button';
        fillBtn.className = 'lobby-player-action-btn lobby-player-fill-btn';
        fillBtn.textContent = reservedBotSlot ? 'Use Bot' : '+ Bot';
        fillBtn.title = reservedBotSlot ? 'Join this slot as a bot' : 'Fill this slot with a bot';
        fillBtn.disabled = lobbyRuntime.botActionInFlight;
        fillBtn.addEventListener('click', async (event) => {
          event.preventDefault();
          event.stopPropagation();
          if (lobbyRuntime.botActionInFlight) return;
          lobbyRuntime.botActionInFlight = true;
          lobby.message = reservedBotSlot ? 'Joining the reserved slot as a bot...' : 'Adding a bot to fill the slot...';
          renderLobby();
          try {
            await addHostBotViaApi(lobby.roomId, i);
          } catch (error) {
            lobby.message = `Could not add bot: ${error?.message || 'unknown error'}`;
            renderLobby();
          } finally {
            lobbyRuntime.botActionInFlight = false;
            renderLobby();
          }
        });
        action.appendChild(fillBtn);
      }
    }
    row.appendChild(name);

    row.appendChild(you);
    row.appendChild(action);

    lobbyPlayerListEl.appendChild(row);
  }
}

function syncLobbyCountdownFromState(nowServer = serverNowMs()) {
  // No room_state is broadcast when a match ends, so lobby.phase can linger on
  // 'running' and leave the waiting room without its controls. Derive the
  // match-over phase from the authoritative status instead. Visibility is left
  // alone — the match-complete overlay stays until the player opens the waiting
  // room themselves; this only makes the controls correct once they do.
  if (lobby.mode === 'room' && lobby.phase === 'running') {
    const endStatus = model.state?.status || authoritative.state?.status || {};
    if (endStatus.gameOver) {
      lobby.phase = lobby.canStart ? 'ready' : 'waiting';
      lobby.message = 'Match complete.';
      if (isLobbyVisible()) renderLobby();
      return;
    }
  }
  if (!net.connected || !lobby.canStart || lobby.mode !== 'room') return;
  if (lobby.startMode === 'host' && lobby.phase !== 'starting' && lobby.phase !== 'running') {
    return;
  }
  if (lobby.phase === 'running' && !isLobbyVisible()) {
    return;
  }
  // Peeking at the waiting room mid-match: never auto-hide it from here.
  if (lobbyRuntime.matchLobbyPeek && lobby.phase === 'running') {
    return;
  }

  const status = model.state?.status || authoritative.state?.status || {};
  if (status.gameOver || (status.waitingNext && Number(status.nextRoundAt) > 0)) {
    return;
  }
  const running = Boolean(status.running) && !status.paused && !status.gameOver;
  if (!running) {
    const pendingMessage = 'Match starting...';
    const wasVisible = isLobbyVisible();
    const messageChanged = lobby.message !== pendingMessage;
    lobby.phase = 'starting';
    lobby.message = pendingMessage;
    if (!wasVisible) {
      setLobbyVisible(true);
      lobbyDeps.clearInput();
    }
    if (!wasVisible || messageChanged) {
      renderLobby();
    }
    return;
  }

  if (isLobbyVisible()) {
    // Hold the scene switch until "Go!" has been on screen for its beat. This
    // runs every frame from the main loop, so returning here simply retries.
    if (countdownGoHoldRemainingMs() > 0) return;
    countdownEndsAtLocalMs = 0;
    lobby.phase = 'running';
    setLobbyVisible(false);
    setStatus(`Connected: room "${net.roomId}" as ${net.playerName}`);
  }
}

function applyRoomState(data) {
  if (lobbyRuntime.returnToLobbyInProgress) return;
  if (!data || typeof data !== 'object') return;
  const prevRoomId = sanitizeRoomId(lobby.roomId || net.roomId || '');
  lobby.mode = 'room';
  const roomId = typeof data.roomId === 'string' ? data.roomId.trim() : '';
  if (roomId) {
    lobby.roomId = roomId;
    net.roomId = roomId;
  }
  const effectiveRoomId = sanitizeRoomId(lobby.roomId || net.roomId || '');
  if (effectiveRoomId && effectiveRoomId !== prevRoomId) {
    resetChatFeed(effectiveRoomId, true);
    clearHostRoomSettingsAutosave();
  }
  const roomName = typeof data.roomName === 'string' ? data.roomName.trim() : '';
  if (roomName) {
    lobby.roomName = roomName;
  }
  const hostName = typeof data.hostName === 'string' ? data.hostName.trim() : '';
  lobby.hostName = hostName;
  lobby.isHost = sameHostName(hostName, net.playerName);

  const maxPlayers = Number(data.maxPlayers);
  if (Number.isFinite(maxPlayers) && maxPlayers > 0) {
    lobby.maxPlayers = Math.max(1, Math.floor(maxPlayers));
  }

  const minPlayers = Number(data.minPlayersToStart);
  if (Number.isFinite(minPlayers) && minPlayers > 0) {
    lobby.minPlayersToStart = Math.max(1, Math.floor(minPlayers));
  }

  lobby.teamMode = Boolean(data.teamMode);
  lobby.startMode = sanitizeStartMode(data.startMode, lobby.startMode || 'host');
  if (
    hostSettingsDraft
    && hostRoomSettingsKey(hostSettingsDraft) === hostRoomSettingsKey(currentHostRoomSettings())
    && !hostSettingsSaveInFlight
    && !hostSettingsAutosaveTimer
  ) {
    hostSettingsDraft = null;
  }
  const players = Array.isArray(data.players)
    ? data.players
      .map((player) => ({
        slotIndex: Number.isFinite(Number(player?.slotIndex)) ? Number(player.slotIndex) : -1,
        playerId: Number.isFinite(Number(player?.playerId)) ? Number(player.playerId) : -1,
        name: typeof player?.name === 'string' ? player.name.trim().slice(0, 32) : 'Player',
        ownerName: typeof player?.ownerName === 'string' ? player.ownerName.trim().slice(0, 32) : '',
        isBot: Boolean(player?.isBot),
        isSelfBot: Boolean(player?.isSelfBot),
        isClientSlot: player?.isClientSlot === false ? false : true,
        team: normalizeTeam(player?.team),
        botKind: sanitizeBotKind(player?.botKind),
        botIndex: Number.isInteger(player?.botIndex) && player.botIndex >= 0 ? player.botIndex : null,
        muted: Boolean(player?.muted),
        // Absent means present, so an older server (or the pre-match roster,
        // which only ever lists people who are here) is unaffected.
        inRoom: player?.inRoom !== false,
        colorId: Number.isInteger(player?.colorId) && player.colorId >= 0 && player.colorId < PALETTE_SIZE
          ? player.colorId
          : null,
        mesh: Array.isArray(player?.mesh)
          ? player.mesh.filter((stop) => typeof stop === 'string' && /^#[0-9a-f]{6}$/i.test(stop)).slice(0, 8)
          : null,
        color: typeof player?.color === 'string' && /^#[0-9a-f]{6}$/i.test(player.color) ? player.color : '',
        colorChosen: Boolean(player?.colorChosen),
        trailOpacityPercent: clampTrailPercent(player?.trailOpacityPercent),
        turnRadiusPercent: normalizePlayerTurnRadiusPercent(player?.turnRadiusPercent)
      }))
      .filter((player) => player.name.length > 0)
    : [];

  lobby.players = players;
  lobby.spectators = Array.isArray(data.spectators)
    ? data.spectators
      .map((spec) => ({
        name: typeof spec?.name === 'string' ? spec.name.trim().slice(0, 32) : '',
        wantsToPlay: Boolean(spec?.wantsToPlay),
        muted: Boolean(spec?.muted)
      }))
      .filter((spec) => spec.name.length > 0)
    : [];
  const myPlayerEntry = lobby.players.find((player) => (
    player.isClientSlot && sameHostName(player.ownerName || player.name, net.playerName)
  )) || null;
  const mySpectatorEntry = lobby.spectators.find((spec) => sameHostName(spec.name, net.playerName)) || null;
  if (myPlayerEntry) applyOwnTurnRadiusPercent(myPlayerEntry.turnRadiusPercent);
  lobby.iAmSpectator = Boolean(mySpectatorEntry && !mySpectatorEntry.wantsToPlay);
  const nextMuted = Boolean((myPlayerEntry || mySpectatorEntry)?.muted);
  if (lobby.iAmMuted !== nextMuted) {
    lobby.iAmMuted = nextMuted;
    ui.controlsDirty = true;
  }

  // Start countdown: map the server deadline onto the local clock using the
  // serverNow carried by the same message, so no clock sync is required.
  const countdownEndsAt = Number(data.startCountdownEndsAt) || 0;
  const countdownStepMs = Number(data.startCountdownStepMs);
  if (Number.isFinite(countdownStepMs) && countdownStepMs > 0) {
    lobby.startCountdownStepMs = Math.max(250, Math.floor(countdownStepMs));
  }
  const stateServerNow = Number(data.serverNow);
  const wasCounting = lobby.startCountdownLocalEndsAt > 0;
  if (countdownEndsAt > 0 && Number.isFinite(stateServerNow)) {
    const remainingMs = Math.max(0, countdownEndsAt - stateServerNow);
    lobby.startCountdownLocalEndsAt = Date.now() + remainingMs;
    // Arm the Go hold now, while the countdown is still running.
    countdownEndsAtLocalMs = lobby.startCountdownLocalEndsAt;
    if (!wasCounting) {
      // Pass how much of the countdown already ran, so only the remaining
      // tick/Go cues get scheduled and the hits stay locked to the displayed
      // numbers even for clients that join (or hear about it) late.
      const durationMs = Number(data.startCountdownDurationMs) || 5000;
      playCountdownSfx({
        elapsedMs: Math.max(0, durationMs - remainingMs),
        durationMs,
        stepMs: lobby.startCountdownStepMs
      });
    }
    ui.suppressStartSfxUntil = Date.now() + remainingMs + COUNTDOWN_GO_TAIL_MS;
  } else {
    lobby.startCountdownLocalEndsAt = 0;
    const rawPhase = typeof data.phase === 'string' ? data.phase.toLowerCase() : '';
    const matchIsStarting = rawPhase === 'running' || rawPhase === 'starting';
    if (wasCounting && !matchIsStarting) {
      // Stopped, not finished: disarm the hold so nothing lingers on "Go!".
      countdownEndsAtLocalMs = 0;
      stopCountdownSfx();
    }
  }
  const stopsLeft = Number(data.countdownStopsLeft);
  if (Number.isFinite(stopsLeft)) {
    lobby.countdownStopsLeft = Math.max(0, Math.floor(stopsLeft));
  }

  // Leaderboard placings arrive with the room_state the server sends right
  // after recording a finished match, which is after the final standings card
  // has already been built -- so mark the overlay dirty to fold them in.
  const boardRanks = Array.isArray(data.matchBoardRanks)
    ? data.matchBoardRanks
      .map((entry) => ({ id: Number(entry?.id), rank: Number(entry?.rank) }))
      .filter((entry) => Number.isFinite(entry.id) && entry.rank > 0)
    : [];
  const boardRanksSig = boardRanks.map((entry) => `${entry.id}:${entry.rank}`).join('|');
  if (boardRanksSig !== lobby.matchBoardRanks.map((entry) => `${entry.id}:${entry.rank}`).join('|')) {
    lobby.matchBoardRanks = boardRanks;
    ui.overlayDirty = true;
  }
  lobby.matchBoardSize = Math.max(0, Math.floor(Number(data.matchBoardSize) || 0));

  lobbyPendingColorPick = null;
  const connectedPlayers = Number(data.connectedPlayers);
  lobby.connectedPlayers = Number.isFinite(connectedPlayers)
    ? Math.max(0, Math.floor(connectedPlayers))
    : players.length;
  const connectedClients = Number(data.connectedClients);
  lobby.connectedClients = Number.isFinite(connectedClients)
    ? Math.max(0, Math.floor(connectedClients))
    : players.filter((player) => player.isClientSlot).length;
  const humanPlayers = Number(data.humanPlayers);
  lobby.humanPlayers = Number.isFinite(humanPlayers)
    ? Math.max(0, Math.floor(humanPlayers))
    : players.filter((player) => !player.isBot).length;
  const botPlayers = Number(data.botPlayers);
  lobby.botPlayers = Number.isFinite(botPlayers)
    ? Math.max(0, Math.floor(botPlayers))
    : players.filter((player) => player.isBot).length;
  const manualBotCount = Number(data.manualBotCount);
  lobby.manualBotCount = Number.isFinite(manualBotCount)
    ? Math.max(0, Math.floor(manualBotCount))
    : players.filter((player) => player.isBot && player.botKind === 'manual').length;

  // Who is HERE, which is not the same as how many seats are taken -- so this
  // deliberately reads lower than the PLAYERS stat in the card whenever the
  // host has added bots. Host bots hold seats but are nobody. A player who has
  // handed their own snake to the bot still counts: they are in the room and
  // they can talk. Set here rather than in renderLobby because the in-game side
  // panel wears the badge too, and the lobby is not rendered then.
  setRoomPresenceCount(Math.max(0, lobby.connectedPlayers - lobby.manualBotCount));

  // Engine ids shift as people join and leave, so the renderer's copy is
  // re-derived from the authoritative roster every time it changes.
  publishSlotTrailOpacities();

  if (Array.isArray(data.chatHistory)) {
    applyChatHistory(data.chatHistory, effectiveRoomId);
  }

  // The server's verdict wins whenever it sends one. Re-deriving it from the
  // count and OR-ing the two together used to be harmless, because the count
  // WAS the rule -- but a team room also has to be even, so that fallback
  // offered Start on a 3v2 the server would refuse to launch.
  lobby.canStart = data.canStart === undefined
    ? lobby.connectedPlayers >= lobby.minPlayersToStart
    : Boolean(data.canStart);
  const phaseRaw = typeof data.phase === 'string' ? data.phase.toLowerCase() : '';
  if (phaseRaw === 'running' || phaseRaw === 'starting' || phaseRaw === 'ready' || phaseRaw === 'waiting') {
    lobby.phase = phaseRaw;
  } else {
    lobby.phase = lobby.canStart ? 'ready' : 'waiting';
  }
  // A just-finished match keeps its final overlay on the arena until the player
  // leaves, exactly like a live one — so a background room_state (a spectator
  // joining, someone leaving) must not pop the waiting room over it. The server
  // flag is authoritative and immune to snapshot/room_state message ordering;
  // the local status is a fallback for older servers.
  const matchComplete = Boolean(data.matchComplete) || Boolean(model.state?.status?.gameOver);
  // Rejoined specifically to see the waiting room: claim the intent as soon as
  // the match is confirmed live, before the clear below can drop it.
  if (lobbyRuntime.openWaitingRoomAfterJoin && (lobby.phase === 'running' || matchComplete)) {
    lobbyRuntime.openWaitingRoomAfterJoin = false;
    lobbyRuntime.matchLobbyPeek = true;
  }
  if (lobby.phase !== 'running' && !matchComplete) {
    lobbyRuntime.matchLobbyPeek = false;
  }
  if (lobby.phase === 'running' || matchComplete) {
    // A live match (engine running) or one whose final overlay still owns the
    // arena must NOT be interrupted by a background room_state. Most notably a
    // mid-match disconnect drops the count below minPlayersToStart and flips
    // canStart false; that must not bounce a racing player to the waiting room.
    // Keep the arena view (unless we're deliberately peeking) and never release
    // held keys. lobby.phase stays whatever the server reported ('running' or,
    // post-match, 'ready'/'waiting'), so the waiting-room controls are correct
    // once the player opens it. Checked before canStart so the match wins
    // regardless of the current headcount.
    lobby.message = lobby.phase === 'running' ? 'Match in progress.' : 'Match complete.';
    const keepArenaView = !lobbyRuntime.matchLobbyPeek && !isLobbyVisible();
    if (!keepArenaView) {
      setLobbyVisible(true);
      if (net.connected) {
        lobbyDeps.clearInput();
      }
    }
  } else if (lobby.canStart) {
    if (lobby.phase === 'waiting') {
      lobby.phase = lobby.startMode === 'host' ? 'ready' : 'starting';
    }
    lobby.message = lobby.phase === 'ready'
      ? (lobby.isHost ? 'Players ready. Start when ready.' : 'Waiting for host to start...')
      : 'Match starting...';
    setLobbyVisible(true);
    if (net.connected) {
      lobbyDeps.clearInput();
    }
  } else {
    lobby.phase = 'waiting';
    const needed = lobbyPlayersNeeded();
    lobby.message = needed > 0
      ? `Waiting for ${needed} more player${needed === 1 ? '' : 's'}...`
      : 'Waiting for match start...';
    setLobbyVisible(true);
    if (net.connected) {
      lobbyDeps.clearInput();
    }
  }

  renderLobby();
  maybeNotifyHostRoomReady();
  syncLobbyCountdownFromState(serverNowMs());
}

// The room filling up is the one waiting-room event the host has to act on, and
// the one they are least likely to be watching for -- so tell them when they are
// looking somewhere else. The 'ready' phase is exactly "startable and waiting on
// the host": an auto-start room reports 'starting' instead and announces itself
// with the countdown a second later.
//
// Fires on the edge into ready and latches, so a room that stays full does not
// nag; the latch clears the moment the room stops being startable. Nothing plays
// when the host is already looking at the waiting room, and the latch is set
// either way, so tabbing away afterwards stays silent -- the notification is
// about the moment the room became ready, not about being away.
//
// Which ready moments are worth announcing at all is roomReadyIsAnnounceable's
// call -- a solo room and a min-1 room are startable while the host is the only
// one in them, so for those the cue waits for the room to FILL instead.
function maybeNotifyHostRoomReady() {
  const announceable = roomReadyIsAnnounceable(lobby);
  const ready = announceable && lobby.isHost && lobby.phase === 'ready' && isLobbyVisible();
  if (!ready) {
    hostReadyNotified = false;
    return;
  }
  if (hostReadyNotified) return;
  hostReadyNotified = true;
  const focused = typeof document.hasFocus !== 'function' || document.hasFocus();
  if (focused) return;
  playSound(SFX.ready, HOST_READY_SFX_VOLUME);
}

export {
  setLobbyDeps,
  sanitizeRoomId,
  generateRoomId,
  sanitizeStartMode,
  sanitizeBotKind,
  clampRoomPlayers,
  fetchRoomDirectory,
  createRoomViaApi,
  updateRoomSettingsViaApi,
  requestRoomStartViaApi,
  addHostBotViaApi,
  removeHostBotViaApi,
  setSelfBotModeViaApi,
  joinRoom,
  enterRoomById,
  openRoomPasswordPrompt,
  returnToLobbyBrowser,
  isLobbyVisible,
  setLobbyVisible,
  openWaitingRoomPeek,
  syncStageVisibility,
  lobbyPlayersNeeded,
  lobbyReadyLabel,
  setLobbyCopyButtonLabel,
  setupLobbyUi,
  renderLobby,
  syncLobbyCountdownFromState,
  countdownGoHoldRemainingMs,
  renderLobbyStartCountdown,
  applyRoomState,
  handleColorTaken,
  handleTrailOpacityChanged,
  handleTurnRadiusChanged,
  applyOwnTurnRadiusPercent
};
